// Area clienti (clienti finali) di JV Service: accesso con codice via SMS o email, pratiche e documenti
// condivisi, promemoria, news sui servizi, contatti dello studio. Pensata come app per il telefono.
// Token SEPARATO da quello del personale (chiave diversa in localStorage, richieste con fetch e non con
// axios, che porta il token dello staff).
import React, { useCallback, useEffect, useState } from 'react';
import { stileServizio } from '../components/areaClienti/serviziIcone';
import { tr, tz, lingua, impostaLingua, linguaIniziale } from './areaClientiLingua';
import { BASE, API, chiama } from './areaClientiApi';
import Annunci from '../components/areaClienti/Annunci';
import DiventaCliente from '../components/areaClienti/DiventaCliente';
import {
  Smartphone, Mail, ShieldCheck, ChevronRight, ChevronLeft, FileText, CheckCircle2, Clock, AlertTriangle,
  BellRing, FolderOpen, LogOut, Phone, MapPin, Loader2,
  MessageCircle, Send, Plus, Check, Info, Search, Euro, ListChecks, Fingerprint, Trash2, UserRound, CalendarDays, Download, Share2, Eye, X, Maximize2, Megaphone, Settings, LayoutGrid, Archive, Store,
} from 'lucide-react';

const CHIAVE = 'jv_area_clienti_token';

const CHIAVE_PRIVACY = 'jv_area_clienti_privacy';
// scelta sui consensi fatta su questo telefono (serve prima dell'accesso; dopo l'accesso vale quella salvata dallo studio)
const leggiPrivacy = () => { try { return JSON.parse(localStorage.getItem(CHIAVE_PRIVACY) || 'null'); } catch (_) { return null; } };
const salvaPrivacy = (c) => { try { localStorage.setItem(CHIAVE_PRIVACY, JSON.stringify(c)); } catch (_) { /* privato */ } };

// ---- impronta / Face ID (WebAuthn) ----
// Su questo telefono resta solo il riferimento alla chiave (la chiave vera è nel telefono, protetta da
// impronta/volto). Con l'impronta attiva la sessione dura finché l'app/la scheda è aperta: alla
// riapertura si entra con l'impronta. Telefono nuovo o app reinstallata = niente riferimento = codice.
const CHIAVE_BIO = 'jv_area_clienti_impronta';
const CHIAVE_BIO_NO = 'jv_area_clienti_impronta_no';
// Dentro l'app JV Service (Android/iPhone) l'impronta la gestisce l'app: la pagina le chiede le cose
// con postMessage e riceve la risposta con l'evento "jvapp". window.JV_APP lo imposta l'app all'avvio.
const inApp = () => typeof window !== 'undefined' && !!window.ReactNativeWebView;
function chiediApp(msg) {
  return new Promise((ok, ko) => {
    const fine = setTimeout(() => { window.removeEventListener('jvapp', h); ko(new Error(tr("L'app non risponde"))); }, 120000);
    function h(e) {
      const d = e.detail || {};
      if (d.risposta !== msg.tipo) return;
      clearTimeout(fine); window.removeEventListener('jvapp', h);
      if (d.errore) { const err = new Error(d.errore); if (d.annullato) err.name = 'NotAllowedError'; if (d.stato) err.status = d.stato; ko(err); } else ok(d);
    }
    window.addEventListener('jvapp', h);
    window.ReactNativeWebView.postMessage(JSON.stringify(msg));
  });
}
const leggiBio = () => {
  if (inApp()) return window.JV_APP?.dispositivo ? { id: window.JV_APP.dispositivo, contatto: window.JV_APP.contatto || '', app: true } : null;
  try { return JSON.parse(localStorage.getItem(CHIAVE_BIO) || 'null'); } catch (_) { return null; }
};
const salvaBio = (b) => { if (inApp()) return; try { if (b) localStorage.setItem(CHIAVE_BIO, JSON.stringify(b)); else localStorage.removeItem(CHIAVE_BIO); } catch (_) { /* privato */ } };
async function bioDisponibile() {
  if (inApp()) return !!window.JV_APP?.biometria;
  try {
    const { biometria_rp_id: rp } = await chiama('/stato');   // dominio su cui vale l'impronta
    return window.location.hostname === rp && !!window.PublicKeyCredential
      && await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) { return false; }
}
const daB64u = (s) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0));
const aB64u = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
async function creaImpronta(o) {
  const cred = await navigator.credentials.create({ publicKey: {
    ...o, challenge: daB64u(o.challenge), user: { ...o.user, id: daB64u(o.user.id) },
    excludeCredentials: (o.excludeCredentials || []).map((c) => ({ ...c, id: daB64u(c.id) })),
  } });
  return { id: cred.id, rawId: aB64u(cred.rawId), type: cred.type,
    response: { clientDataJSON: aB64u(cred.response.clientDataJSON), attestationObject: aB64u(cred.response.attestationObject),
      transports: cred.response.getTransports ? cred.response.getTransports() : [] } };
}
async function usaImpronta(o) {
  const cred = await navigator.credentials.get({ publicKey: {
    ...o, challenge: daB64u(o.challenge), allowCredentials: (o.allowCredentials || []).map((c) => ({ ...c, id: daB64u(c.id) })),
  } });
  const r = cred.response;
  return { id: cred.id, rawId: aB64u(cred.rawId), type: cred.type,
    response: { clientDataJSON: aB64u(r.clientDataJSON), authenticatorData: aB64u(r.authenticatorData), signature: aB64u(r.signature),
      userHandle: r.userHandle ? aB64u(r.userHandle) : null } };
}
async function attivaImpronta(token, contatto) {
  if (inApp()) {
    const r = await chiediApp({ tipo: 'attiva_impronta', token, contatto });
    window.JV_APP = { ...(window.JV_APP || {}), dispositivo: r.id, contatto };
    try { sessionStorage.setItem(CHIAVE, token); localStorage.removeItem(CHIAVE); } catch (_) { /* privato */ }
    return;
  }
  const o = await chiama('/biometria/registra/opzioni', { metodo: 'POST', token });
  const credenziale = await creaImpronta(o.opzioni);
  const r = await chiama('/biometria/registra/verifica', { metodo: 'POST', token, corpo: { sfida: o.sfida, credenziale } });
  salvaBio({ credential_id: r.credential_id, id: r.id, contatto });
  // da ora la sessione non resta salvata: alla prossima apertura si entra con l'impronta
  try { sessionStorage.setItem(CHIAVE, token); localStorage.removeItem(CHIAVE); } catch (_) { /* privato */ }
}

const leggiToken = () => { try { return sessionStorage.getItem(CHIAVE) || localStorage.getItem(CHIAVE) || ''; } catch (_) { return ''; } };
const salvaToken = (t) => {
  try {
    sessionStorage.removeItem(CHIAVE); localStorage.removeItem(CHIAVE);
    if (t) (leggiBio() ? sessionStorage : localStorage).setItem(CHIAVE, t);
  } catch (_) { /* privato */ }
};


// icona e colore di ogni servizio
// elenco servizi (nome, icona, colore, riquadro) gestito dallo studio: caricato all'avvio
let ELENCO_SERVIZI = [];
const servizio = (nome) => stileServizio(ELENCO_SERVIZI.find((x) => x.nome === nome));
// nome del servizio nella lingua scelta: quello tradotto nel CRM, altrimenti i nomi di sempre
const NOMI_SERVIZI_ZH = {
  'Canone RAI': 'Canone RAI（电视费）', 'Permessi di soggiorno': '居留许可', 'Ospitalità': '住宿登记', 'Luce e gas': '水电燃气',
  'Assicurazioni': '保险', 'Telefonia': '电话网络', 'Pratiche e servizi': '事务与服务', 'Lo studio': '事务所', 'Top': '精选',
  'Patronato': 'Patronato（劳工援助）', 'Caf': 'CAF', 'CAF': 'CAF',
};
const nomeServizio = (nome) => (lingua() === 'zh'
  ? (ELENCO_SERVIZI.find((x) => x.nome === nome)?.nome_zh || NOMI_SERVIZI_ZH[nome] || nome) : nome);

// scelta della lingua (Impostazioni e finestra privacy): avvisa tutta l'app con un evento
function SceltaLingua({ piccola }) {
  const cambia = (l) => window.dispatchEvent(new CustomEvent('jv:lingua', { detail: l }));
  return (
    <div className={`inline-flex rounded-full bg-slate-100 p-0.5 ${piccola ? 'text-[12px]' : 'text-[14px]'}`}>
      {[['it', 'Italiano'], ['zh', '中文']].map(([k, nome]) => (
        <button key={k} type="button" onClick={() => cambia(k)}
          className={`rounded-full ${piccola ? 'px-2.5 py-1' : 'px-4 py-1.5'} font-medium ${lingua() === k ? 'bg-white shadow text-green-800' : 'text-slate-500'}`}>{nome}</button>
      ))}
    </div>
  );
}
const TITOLO = { fontFamily: 'Manrope, sans-serif' };

function IconaServizio({ nome, grande }) {
  const { icona: Icona, colore } = servizio(nome);
  return (
    <div className={`${colore} ${grande ? 'w-12 h-12 rounded-2xl' : 'w-8 h-8 rounded-lg'} flex items-center justify-center shrink-0`}>
      <Icona className={grande ? 'w-6 h-6' : 'w-4 h-4'} />
    </div>
  );
}

function Errore({ testo }) {
  if (!testo) return null;
  return (
    <div className="mt-3 flex gap-2 items-start rounded-xl bg-red-50 text-red-800 px-3 py-2.5 text-[15px]">
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" /> <span>{testo}</span>
    </div>
  );
}

// ---------------- accesso ----------------
function Accesso({ onEntrato, consensi, studio }) {
  const [bio, setBio] = useState(leggiBio());
  const [passo, setPasso] = useState(leggiBio() ? 'impronta' : 'contatto');
  const [canale, setCanale] = useState('sms');
  const [cellulare, setCellulare] = useState('');
  const [email, setEmail] = useState('');
  const [codice, setCodice] = useState('');
  const [errore, setErrore] = useState('');
  const [info, setInfo] = useState('');
  const [attesa, setAttesa] = useState(false);
  // consensi chiesti qui, quando si decide di entrare (Apple 5.1.1: niente dati prima di un'azione che li richiede)
  const [letto, setLetto] = useState(false);
  const [leggi, setLeggi] = useState(false);
  const [scelte, setScelte] = useState({ news: !!consensi?.news, promemoria: !!consensi?.promemoria });

  const chi = canale === 'email' ? { email: email.trim() } : { cellulare };
  const valido = canale === 'email' ? /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()) : cellulare.replace(/\D/g, '').length >= 9;

  const chiediCodice = async (e) => {
    e?.preventDefault();
    setErrore(''); setAttesa(true);
    try {
      const r = await chiama('/accesso/codice', { metodo: 'POST', corpo: chi });
      setInfo(r.messaggio); setPasso('codice');
    } catch (err) { setErrore(err.message); } finally { setAttesa(false); }
  };
  const verifica = async (e) => {
    e.preventDefault();
    setErrore(''); setAttesa(true);
    try {
      const r = await chiama('/accesso/verifica', { metodo: 'POST', corpo: { ...chi, codice, consensi: { privacy: true, ...scelte } } });
      salvaPrivacy({ versione: studio?.versione_privacy, ...scelte });
      salvaToken(r.token); onEntrato(r.token);
    } catch (err) { setErrore(err.message); } finally { setAttesa(false); }
  };

  const entraImpronta = async () => {
    setErrore(''); setAttesa(true);
    try {
      let r;
      if (bio.app) r = await chiediApp({ tipo: 'entra_impronta' });
      else {
        const o = await chiama('/biometria/accesso/opzioni', { metodo: 'POST', corpo: { credential_id: bio.credential_id } });
        const credenziale = await usaImpronta(o.opzioni);
        r = await chiama('/biometria/accesso/verifica', { metodo: 'POST', corpo: { sfida: o.sfida, credenziale } });
      }
      salvaToken(r.token); onEntrato(r.token);
    } catch (err) {
      if (err.status === 404 || err.status === 401) { salvaBio(null); setBio(null); setPasso('contatto'); setErrore(err.message); }
      else if (err.name === 'NotAllowedError') setErrore(tr('Impronta annullata. Riprova o entra con il codice.'));
      else setErrore(err.message || tr('Accesso non riuscito'));
    } finally { setAttesa(false); }
  };

  const inputCls = 'w-full mt-2 text-xl px-4 py-3.5 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 transition';
  const bottoneCls = 'w-full mt-4 py-3.5 rounded-2xl text-[17px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 shadow-lg shadow-green-900/20 active:scale-[0.99] transition disabled:opacity-40 disabled:shadow-none flex items-center justify-center gap-2';

  return (
    <div className="bg-white rounded-3xl shadow-xl shadow-slate-900/5 p-5 relative">
      {passo === 'impronta' && bio ? (
        <div className="text-center">
          <h2 className="text-[22px] font-bold text-slate-900" style={TITOLO}>{tr('Bentornato')}</h2>
          <p className="text-slate-500 text-[15px] mt-1">{bio.contatto}</p>
          <button onClick={entraImpronta} disabled={attesa}
            className="mx-auto mt-5 w-24 h-24 rounded-full bg-gradient-to-br from-green-800 to-emerald-500 text-white flex items-center justify-center shadow-lg shadow-green-900/20 active:scale-95 transition disabled:opacity-50">
            {attesa ? <Loader2 className="w-10 h-10 animate-spin" /> : <Fingerprint className="w-12 h-12" />}
          </button>
          <button onClick={entraImpronta} disabled={attesa} className={bottoneCls}>{tr('Entra con impronta o Face ID')}</button>
          <button type="button" className="w-full mt-3 text-[15px] text-green-800 font-medium" onClick={() => { setErrore(''); setPasso('contatto'); }}>
            Entra con il codice (SMS o email)
          </button>
        </div>
      ) : passo === 'contatto' ? (
        <form onSubmit={chiediCodice}>
          <h2 className="text-[22px] font-bold text-slate-900" style={TITOLO}>{tr('Entra nell\'area clienti')}</h2>
          <p className="text-slate-500 text-[15px] mt-1">{tr('Ricevi un codice di accesso: niente password da ricordare. Se sei già nostro cliente vedrai anche le tue pratiche.')}</p>
          <div className="grid grid-cols-2 gap-1 mt-4 p-1 bg-slate-100 rounded-2xl">
            {[['sms', tr('Via SMS'), Smartphone], ['email', tr('Via email'), Mail]].map(([k, t, Icona]) => (
              <button key={k} type="button" onClick={() => { setCanale(k); setErrore(''); }}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-[15px] font-medium transition ${canale === k ? 'bg-white text-green-800 shadow' : 'text-slate-500'}`}>
                <Icona className="w-4 h-4" /> {t}
              </button>
            ))}
          </div>
          {canale === 'sms' ? (
            <label className="block mt-4 text-sm text-slate-600">{tr('Il tuo cellulare (se sei cliente, quello lasciato allo studio)')}
              <input className={inputCls} type="tel" inputMode="tel" autoComplete="tel" placeholder="328 123 4567"
                value={cellulare} onChange={(e) => setCellulare(e.target.value)} />
            </label>
          ) : (
            <label className="block mt-4 text-sm text-slate-600">{tr('La tua email (se sei cliente, quella lasciata allo studio)')}
              <input className={inputCls} type="email" inputMode="email" autoComplete="email" placeholder="nome@email.it"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
          )}
          <div className="mt-4 rounded-2xl border border-slate-200 px-3 divide-y divide-slate-100">
            <label className="flex items-start gap-3 py-3">
              <input type="checkbox" className="mt-1 w-5 h-5 accent-green-700" checked={letto} onChange={(e) => setLetto(e.target.checked)} />
              <span className="text-[14px] text-slate-700 leading-snug">
                {tr("Ho letto l'informativa privacy: i dati servono per entrare, vedere le pratiche e scrivere allo studio.")}{' '}
                <button type="button" className="text-green-800 font-semibold underline" onClick={() => setLeggi(true)}>{tr("Leggi l'informativa")}</button>
              </span>
            </label>
            {['news', 'promemoria'].map((k) => (
              <Interruttore key={k} attivo={scelte[k]} onCambia={(v) => setScelte({ ...scelte, [k]: v })}
                titolo={<>{tr(TESTI_CONSENSI[k][0])} <span className="ml-1 text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 align-middle">{tr('facoltativo')}</span></>}
                testo={tr(TESTI_CONSENSI[k][1])} />
            ))}
          </div>
          <button className={bottoneCls} disabled={attesa || !valido || !letto}>
            {attesa ? <Loader2 className="w-5 h-5 animate-spin" /> : null} {tr('Ricevi il codice')}
          </button>
          <button type="button" disabled={!valido || !letto}
            className="w-full mt-3 text-[15px] text-green-800 font-medium disabled:text-slate-300"
            onClick={() => { setErrore(''); setInfo(tr('Inserisci il codice che ti ha dato lo studio.')); setPasso('codice'); }}>
            {tr('Ho già un codice dallo studio')}
          </button>
        </form>
      ) : (
        <form onSubmit={verifica}>
          <button type="button" className="flex items-center text-slate-500 text-sm -ml-1"
            onClick={() => { setPasso('contatto'); setCodice(''); setInfo(''); setErrore(''); }}>
            <ChevronLeft className="w-4 h-4" /> {tr('Indietro')}
          </button>
          <h2 className="text-[22px] font-bold text-slate-900 mt-2" style={TITOLO}>{tr('Inserisci il codice')}</h2>
          {info && <p className="mt-2 rounded-xl bg-green-50 text-green-900 px-3 py-2.5 text-[15px]">{info}</p>}
          <input className={`${inputCls} text-center tracking-[0.5em] font-semibold text-2xl`} inputMode="numeric" autoComplete="one-time-code"
            maxLength={6} placeholder="••••••" value={codice} onChange={(e) => setCodice(e.target.value.replace(/\D/g, ''))} autoFocus />
          <button className={bottoneCls} disabled={attesa || codice.length !== 6}>
            {attesa ? <Loader2 className="w-5 h-5 animate-spin" /> : null} {tr('Entra')}
          </button>
        </form>
      )}
      <Errore testo={errore} />
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
        <ShieldCheck className="w-4 h-4 text-green-700" /> {tr('I tuoi dati restano sui server di JV Service.')}
      </div>
      {leggi && <FinestraInformativa studio={studio} onChiudi={() => { setLeggi(false); setLetto(true); }} testoChiudi={tr('Ho letto')} />}
    </div>
  );
}

// Informativa a tutto schermo (dall'accesso, da Contatti, dalle Impostazioni)
function FinestraInformativa({ studio, onChiudi, testoChiudi }) {
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-end sm:items-center justify-center" onClick={onChiudi}>
      <div className="bg-white w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
        <Informativa studio={studio} />
        <button type="button" onClick={onChiudi}
          className="w-full mt-5 py-3.5 rounded-2xl text-[16px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600">{testoChiudi || tr('Chiudi')}</button>
      </div>
    </div>
  );
}

// Pagina pubblica "come eliminare l'account" (Google Play: link web per la richiesta di cancellazione)
function PaginaEliminaAccount() {
  const [studio, setStudio] = useState(null);
  useEffect(() => { chiama('/studio').then(setStudio).catch(() => setStudio({})); }, []);
  const H = ({ children }) => <div className="mt-4 font-semibold text-slate-900">{children}</div>;
  return (
    <div className="min-h-screen bg-[#f3f6f4] py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-sm p-6 text-[15px] text-slate-600 leading-snug">
        <div className="text-[20px] font-bold text-slate-900" style={TITOLO}>{tr("Eliminare l'account dell'app JV Service")}</div>
        <H>{tr("Dall'app")}</H>
        <p>{tr("Entra nell'app, tocca ⚙ Impostazioni in alto a destra e poi \"Elimina account\". L'eliminazione è immediata.")}</p>
        <H>{tr("Senza l'app")}</H>
        <p>{tr("Scrivi a")} {studio?.email ? <a className="text-green-800 font-semibold" href={`mailto:${studio.email}?subject=Eliminazione account app`}>{studio.email}</a> : tr('lo studio')}{' '}
          {tr("dal recapito con cui entri nell'app (o chiamaci{tel}), chiedendo l'eliminazione dell'account. Lo facciamo entro 30 giorni.", { tel: studio?.telefono ? ` al ${studio.telefono}` : '' })}</p>
        <H>{tr('Cosa viene cancellato')}</H>
        <p>{tr("L'accesso all'app, l'impronta / Face ID registrati, i consensi dati nell'app, le notifiche e, se non sei nostro cliente, i messaggi inviati.")}</p>
        <H>{tr('Cosa resta')}</H>
        <p>{tr('Le pratiche, i documenti e la corrispondenza che lo studio deve conservare per legge (di norma 10 anni). Puoi chiederne la cancellazione allo studio: la valutiamo secondo il GDPR.')}</p>
        <a href="/area-clienti?privacy" className="inline-block mt-6 text-green-800 font-semibold">{tr('Informativa privacy')}</a>
      </div>
    </div>
  );
}

// Pagina pubblica dell'informativa (link per App Store / Play Store): clienti.crm-jv.com/area-clienti?privacy
function PaginaPrivacy() {
  const [studio, setStudio] = useState(null);
  useEffect(() => { chiama('/studio').then(setStudio).catch(() => setStudio({})); }, []);
  return (
    <div className="min-h-screen bg-[#f3f6f4] py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-sm p-6">
        {studio ? <Informativa studio={studio} /> : <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
        <a href="/area-clienti" className="inline-block mt-6 text-green-800 font-semibold">{tr("Vai all'area clienti")}</a>
      </div>
    </div>
  );
}

// ---------------- pratiche: servizio → pratiche (dalla più recente) → documenti → anteprima ----------------
function Promemoria({ m, onNascondi }) {
  return (
    <div className="rounded-3xl p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 flex gap-3 shadow-sm">
      <div className="w-10 h-10 rounded-xl bg-amber-400/20 text-amber-700 flex items-center justify-center shrink-0"><BellRing className="w-5 h-5" /></div>
      <div>
        <div className="text-[12px] font-semibold tracking-wide text-amber-800 uppercase">{tr('Promemoria')} · {nomeServizio(m.servizio)}</div>
        <div className="text-[15px] text-slate-800 mt-1 leading-snug">{m.testo}</div>
        <div className="text-xs text-slate-500 mt-1">{m.persona}</div>
        <button onClick={() => onNascondi(m.id)}
          className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-amber-900 bg-white/70 border border-amber-200 rounded-full px-3 py-1">
          <Check className="w-4 h-4" /> {tr('Già fatto, non avvisarmi più')}
        </button>
      </div>
    </div>
  );
}

function StatoPratica({ p }) {
  return (
    <div className={`inline-flex items-center gap-1.5 text-[13px] font-medium px-2.5 py-1 rounded-full ${p.ok ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
      {p.ok ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />} {p.stato}
    </div>
  );
}

// file → Scarica / Condividi. Nell'app lo fa l'app (foglio di condivisione del telefono), sul sito il browser.
async function scaricaFile(token, url) {
  const r = await fetch(`${BASE}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(tr('Documento non disponibile'));
  const nome = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'documento.pdf';
  return { blob: await r.blob(), nome };
}
const aBase64 = (blob) => new Promise((ok, ko) => { const fr = new FileReader(); fr.onload = () => ok(String(fr.result).split(',')[1]); fr.onerror = ko; fr.readAsDataURL(blob); });

function Anteprima({ doc, token, onChiudi }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState('');
  const [attesa, setAttesa] = useState('');
  useEffect(() => {
    chiama(`${doc.url.replace('/api/area-clienti', '')}/anteprima`, { token }).then(setDati).catch((err) => setErrore(err.message));
  }, [doc, token]);
  const azione = async (tipo) => {
    setAttesa(tipo); setErrore('');
    try {
      const { blob, nome } = await scaricaFile(token, doc.url);
      if (inApp()) {
        await chiediApp({ tipo: 'file', azione: tipo, nome, mime: blob.type || 'application/pdf', base64: await aBase64(blob) });
      } else if (tipo === 'condividi' && navigator.canShare && navigator.canShare({ files: [new File([blob], nome, { type: blob.type })] })) {
        await navigator.share({ files: [new File([blob], nome, { type: blob.type })], title: doc.titolo });
      } else {
        const link = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = link; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(link), 5000);
      }
    } catch (err) { if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') setErrore(err.message); } finally { setAttesa(''); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900 flex flex-col">
      <div className="flex items-center gap-2 px-3 py-3 text-white pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <button onClick={onChiudi} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0 text-[15px] font-medium truncate">{doc.titolo}</div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-3">
        {!dati && !errore && <div className="flex justify-center py-20 text-white/60"><Loader2 className="w-8 h-8 animate-spin" /></div>}
        {dati?.pagine.map((src, i) => <img key={i} src={src} alt={`Pagina ${i + 1}`} className="w-full rounded-lg bg-white shadow" />)}
        {dati && <div className="text-center text-white/50 text-xs">{dati.pagine.length} {dati.pagine.length === 1 ? 'pagina' : 'pagine'}</div>}
        {errore && <div className="mx-auto max-w-md"><Errore testo={errore} /></div>}
      </div>
      <div className="grid grid-cols-2 gap-2 p-3 bg-slate-800 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <button onClick={() => azione('scarica')} disabled={!!attesa} className="py-3 rounded-2xl bg-white/10 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          {attesa === 'scarica' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} {tr('Scarica')}</button>
        <button onClick={() => azione('condividi')} disabled={!!attesa} className="py-3 rounded-2xl bg-green-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          {attesa === 'condividi' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />} {tr('Condividi')}</button>
      </div>
    </div>
  );
}

// Riproposta (non insistente) di un consenso facoltativo spento: "No grazie" lo nasconde per 3 mesi.
const PROPOSTE_CONSENSO = {
  promemoria: { icona: BellRing, titolo: 'Vuoi che ti avvisiamo delle scadenze?', pulsante: 'Attiva promemoria',
    testo: 'Ora i promemoria sono spenti: non ti ricorderemo, per esempio, di rinnovare il Canone RAI entro il 31 gennaio.' },
  news: { icona: Megaphone, titolo: 'Vuoi sapere subito di bonus e nuove scadenze?', pulsante: 'Attiva le novità',
    testo: "Ora le novità sono spente: non ti avviseremo di bonus, agevolazioni e nuove scadenze di legge. Potrai solo leggerle nell'app." },
};
function ProponiConsenso({ tipo, token, onAttivato, giaSpento }) {
  const chiave = `jv_area_clienti_no_${tipo}`;
  const [nascosto, setNascosto] = useState(() => {
    try { return Number(localStorage.getItem(chiave) || 0) > Date.now(); } catch (_) { return false; }
  });
  const [spento, setSpento] = useState(giaSpento ?? null);   // null = da verificare
  const [attesa, setAttesa] = useState(false);
  useEffect(() => {
    if (giaSpento !== undefined || nascosto) return;
    chiama('/io', { token }).then((io) => setSpento(!io.consensi?.[tipo])).catch(() => {});
  }, [token, tipo, giaSpento, nascosto]);
  if (nascosto || !spento) return null;
  const { icona: Icona } = PROPOSTE_CONSENSO[tipo];
  const titolo = tr(PROPOSTE_CONSENSO[tipo].titolo), testo = tr(PROPOSTE_CONSENSO[tipo].testo), pulsante = tr(PROPOSTE_CONSENSO[tipo].pulsante);
  const attiva = async () => {
    setAttesa(true);
    try {
      const io = await chiama('/io', { token });
      const c = await chiama('/consensi', { metodo: 'PUT', token, corpo: { privacy: true, news: !!io.consensi?.news, promemoria: !!io.consensi?.promemoria, [tipo]: true } });
      salvaPrivacy({ versione: c.versione, news: c.news, promemoria: c.promemoria });
      setSpento(false); onAttivato && onAttivato();
    } catch (_) { setAttesa(false); }
  };
  const noGrazie = () => {
    try { localStorage.setItem(chiave, String(Date.now() + 90 * 24 * 3600 * 1000)); } catch (_) { /* privato */ }
    setNascosto(true);
  };
  return (
    <div className="bg-white rounded-3xl shadow-sm p-4 border border-amber-200">
      <div className="flex gap-3 items-start">
        <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Icona className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="text-[16px] font-semibold text-slate-900">{titolo}</div>
          <p className="text-[14px] text-slate-600 mt-0.5 leading-snug">{testo}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button onClick={noGrazie} className="py-2.5 rounded-2xl text-[14px] font-semibold text-slate-600 bg-slate-100">{tr('No grazie')}</button>
        <button onClick={attiva} disabled={attesa} className="py-2.5 rounded-2xl text-[14px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 disabled:opacity-50 flex items-center justify-center gap-2">
          {attesa && <Loader2 className="w-4 h-4 animate-spin" />} {pulsante}</button>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">{tr('Puoi cambiare idea quando vuoi da ⚙ Impostazioni → Privacy e consensi.')}</p>
    </div>
  );
}

// ---------------- appuntamenti ISEE / 730 (solo clienti già registrati) ----------------
function Appuntamenti({ token, ospite, soloTipo }) {
  const [dati, setDati] = useState(null);
  const [tipo, setTipo] = useState('');
  const [giorni, setGiorni] = useState(null);
  const [avviso, setAvviso] = useState('');   // prenotazioni chiuse / fuori periodo
  const [giorno, setGiorno] = useState('');
  const [ora, setOra] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState('');
  const [fatto, setFatto] = useState('');
  const carica = useCallback(() => chiama('/appuntamenti', { token }).then(setDati).catch(() => {}), [token]);
  useEffect(() => { carica(); }, [carica]);
  const scegliTipo = async (t) => {
    setTipo(t); setGiorni(null); setGiorno(''); setOra(''); setErrore(''); setFatto('');
    try { const d = await chiama(`/appuntamenti/disponibilita?tipo=${t}`, { token }); setGiorni(d.giorni); setAvviso(d.messaggio || ''); setGiorno(d.giorni[0]?.giorno || ''); }
    catch (err) { setErrore(err.message); setTipo(''); }
  };
  const prenota = async () => {
    setAttesa(true); setErrore('');
    try {
      const r = await chiama('/appuntamenti', { metodo: 'POST', token, corpo: { tipo, inizio: ora } });
      setFatto(tr('Appuntamento confermato: {quando}. Ti abbiamo mandato la conferma.', { quando: r.quando })); setTipo(''); carica();
    } catch (err) { setErrore(err.message); } finally { setAttesa(false); }
  };
  const annulla = async (a) => {
    if (!window.confirm(tr("Annullare l'appuntamento {tipo} di {quando}?", { tipo: a.tipo_nome, quando: a.quando }))) return;
    try { await chiama(`/appuntamenti/${a.id}/annulla`, { metodo: 'POST', token }); carica(); } catch (err) { setErrore(err.message); }
  };
  if (!dati) return null;
  const prossimi = dati.appuntamenti.filter((a) => a.futuro && a.stato === 'fissato' && (!soloTipo || a.tipo === soloTipo));
  const nonCliente = ospite ?? !dati.puo_prenotare;
  const orariGiorno = (giorni || []).find((g) => g.giorno === giorno)?.orari || [];
  return (
    <div className="bg-white rounded-3xl shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="text-[16px] font-semibold text-slate-900">{soloTipo ? tr("Prenota l'appuntamento {tipo}", { tipo: dati.tipi[soloTipo] }) : tr('Appuntamenti ISEE e 730')}</div>
          <div className="text-[13px] text-slate-500">{tr('Prenota quando vuoi: è confermato subito')}</div>
        </div>
      </div>
      {prossimi.map((a) => (
        <div key={a.id} className="mt-3 rounded-2xl bg-blue-50 border border-blue-100 p-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-blue-900">{a.tipo_nome}{a.persona ? ` · ${a.persona}` : ''}</div>
            <div className="text-[15px] text-slate-800">{a.quando.charAt(0).toUpperCase() + a.quando.slice(1)}</div>
          </div>
          <button onClick={() => annulla(a)} className="text-[13px] text-red-600 font-medium">{tr('Annulla')}</button>
        </div>
      ))}
      {nonCliente ? (
        <p className="mt-3 text-[14px] text-slate-600">{tr('Per prenotare devi essere già nostro cliente: scrivici da Messaggi o chiamaci.')}</p>
      ) : !tipo ? (
        <div className="grid grid-cols-2 gap-2 mt-3">
          {Object.entries(dati.tipi).filter(([k]) => (!soloTipo || k === soloTipo) && !prossimi.some((a) => a.tipo === k)).map(([k, t]) => (
            <button key={k} onClick={() => scegliTipo(k)} className="py-3 rounded-2xl border-2 border-green-700 text-green-800 font-semibold text-[15px] active:bg-green-50">{tr('Prenota {tipo}', { tipo: t })}</button>
          ))}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-semibold text-slate-900">{tr('Prenota {tipo}', { tipo: dati.tipi[tipo] })}</div>
            <button onClick={() => setTipo('')} className="text-[13px] text-slate-500">{tr('Annulla')}</button>
          </div>
          {avviso && giorni && giorni.length > 0 && <p className="text-[13px] text-amber-800">{avviso}</p>}
          {giorni === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : giorni.length === 0 ? (
            <p className="text-[14px] text-slate-600 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5">{avviso || tr('Nessun orario libero nei prossimi giorni: chiamaci o scrivici da Messaggi.')}</p>
          ) : (
            <>
              <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 [scrollbar-width:none]">
                {giorni.map((g) => (
                  <button key={g.giorno} onClick={() => { setGiorno(g.giorno); setOra(''); }}
                    className={`shrink-0 rounded-2xl border px-3 py-2 text-center ${giorno === g.giorno ? 'bg-green-800 text-white border-green-800' : 'bg-white text-slate-700 border-slate-200'}`}>
                    <div className="text-[12px] capitalize">{g.nome.split(' ')[0].slice(0, 3)}</div>
                    <div className="text-[15px] font-semibold">{g.nome.split(' ')[1]}</div>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {orariGiorno.map((o) => (
                  <button key={o.inizio} onClick={() => setOra(o.inizio)}
                    className={`py-2 rounded-xl border text-[14px] font-medium ${ora === o.inizio ? 'bg-green-800 text-white border-green-800' : 'bg-white text-slate-700 border-slate-200'}`}>{o.ora}</button>
                ))}
              </div>
              <button onClick={prenota} disabled={!ora || attesa}
                className="w-full py-3 rounded-2xl text-[15px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 disabled:opacity-40 flex items-center justify-center gap-2">
                {attesa && <Loader2 className="w-4 h-4 animate-spin" />} {tr('Conferma appuntamento')}</button>
            </>
          )}
        </div>
      )}
      {fatto && <p className="mt-3 rounded-xl bg-green-50 text-green-900 px-3 py-2.5 text-[14px]">{fatto}</p>}
      <Errore testo={errore} />
    </div>
  );
}

// ---------------- notifiche (campanella in alto) ----------------
function PannelloNotifiche({ token, onChiudi, onLette }) {
  const [dati, setDati] = useState(null);
  useEffect(() => {
    chiama('/notifiche', { token }).then((d) => { setDati(d); if (d.non_lette) chiama('/notifiche/lette', { metodo: 'POST', token }).then(onLette).catch(() => {}); })
      .catch(() => setDati({ notifiche: [] }));
  }, [token, onLette]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center" onClick={onChiudi}>
      <div className="bg-white w-full sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center"><BellRing className="w-5 h-5" /></div>
          <div className="flex-1 text-[18px] font-bold text-slate-900" style={TITOLO}>{tr('Notifiche')}</div>
          <button onClick={onChiudi} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        {!dati ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : dati.notifiche.length === 0
          ? <p className="text-[15px] text-slate-500 py-6 text-center">{tr('Nessuna notifica.')}</p>
          : (
            <div className="divide-y divide-slate-100">
              {dati.notifiche.map((n) => (
                <div key={n.id} className="py-3">
                  <div className="flex items-center gap-2">
                    {!n.letta && <span className="w-2 h-2 rounded-full bg-red-500" />}
                    <span className="text-[15px] font-semibold text-slate-900">{n.titolo}</span>
                  </div>
                  <p className="text-[14px] text-slate-600 mt-0.5 leading-snug">{n.testo}</p>
                  <div className="text-[11px] text-slate-400 mt-1">{n.quando}</div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function Pratiche({ token, onEsci }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState('');
  const [servizioSel, setServizioSel] = useState(null);
  const [pratica, setPratica] = useState(null);
  const [doc, setDoc] = useState(null);

  const carica = useCallback(async () => {
    try { setDati(await chiama('/pratiche', { token })); } catch (err) { if (err.status === 401) onEsci(); else setErrore(err.message); }
  }, [token, onEsci]);
  useEffect(() => { carica(); }, [carica]);
  const nascondi = async (id) => {
    try { await chiama(`/promemoria/${encodeURIComponent(id)}/nascondi`, { metodo: 'POST', token }); carica(); } catch (err) { setErrore(err.message); }
  };
  const vai = (fn) => { fn(); window.scrollTo({ top: 0 }); };

  if (!dati) return <div className="bg-white rounded-3xl flex justify-center py-16 text-slate-400"><Loader2 className="w-7 h-7 animate-spin" /></div>;

  // gruppi per servizio, pratiche già dalla più recente
  const gruppi = [];
  dati.pratiche.forEach((p) => {
    let g = gruppi.find((x) => x.servizio === p.servizio);
    if (!g) { g = { servizio: p.servizio, pratiche: [] }; gruppi.push(g); }
    g.pratiche.push(p);
  });

  // 3° livello: la pratica con i suoi documenti
  if (pratica) {
    return (
      <div className="space-y-4">
        {doc && <Anteprima doc={doc} token={token} onChiudi={() => setDoc(null)} />}
        <div className="bg-white rounded-3xl shadow-sm p-5">
          <button onClick={() => vai(() => setPratica(null))} className="flex items-center text-slate-500 text-sm -ml-1"><ChevronLeft className="w-4 h-4" /> {nomeServizio(pratica.servizio)}</button>
          <div className="flex gap-3 items-start mt-3">
            <IconaServizio nome={pratica.servizio} grande />
            <div className="min-w-0 flex-1">
              <div className="text-[19px] font-bold text-slate-900 leading-tight" style={TITOLO}>{pratica.titolo}</div>
              <div className="text-xs text-slate-500 mt-0.5">{pratica.persona}{pratica.data ? ` · ${pratica.data}` : ''}</div>
              <div className="mt-2"><StatoPratica p={pratica} /></div>
            </div>
          </div>
        </div>
        <h3 className="text-[13px] font-semibold tracking-wider text-slate-500 uppercase px-1">{tr('Documenti')}</h3>
        {pratica.documenti.length === 0 ? (
          <div className="bg-white rounded-3xl p-6 text-center text-slate-500">{tr('I documenti saranno disponibili quando la pratica è completata.')}</div>
        ) : (
          <div className="bg-white rounded-3xl shadow-sm divide-y divide-slate-100 overflow-hidden">
            {pratica.documenti.map((d) => (
              <button key={d.tipo} onClick={() => setDoc(d)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-slate-50">
                <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0"><FileText className="w-5 h-5" /></div>
                <span className="flex-1 text-[15px] text-slate-800 leading-snug">{d.titolo}</span>
                <Eye className="w-5 h-5 text-slate-300" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 2° livello: le pratiche di un servizio
  const gruppo = servizioSel && gruppi.find((g) => g.servizio === servizioSel);
  if (gruppo) {
    return (
      <div className="space-y-3">
        <div className="bg-white rounded-3xl shadow-sm p-4">
          <button onClick={() => vai(() => setServizioSel(null))} className="flex items-center text-slate-500 text-sm -ml-1"><ChevronLeft className="w-4 h-4" /> {tr('Le tue pratiche')}</button>
          <div className="flex items-center gap-3 mt-3">
            <IconaServizio nome={gruppo.servizio} grande />
            <div>
              <h2 className="text-[20px] font-bold text-slate-900 leading-tight" style={TITOLO}>{nomeServizio(gruppo.servizio)}</h2>
              <div className="text-sm text-slate-500">{gruppo.pratiche.length} {gruppo.pratiche.length === 1 ? tr('pratica') : tr('pratiche')}</div>
            </div>
          </div>
        </div>
        {gruppo.pratiche.map((p, i) => (
          <button key={p.id} onClick={() => vai(() => setPratica(p))} className="w-full bg-white rounded-3xl shadow-sm p-4 flex gap-3 items-center text-left active:scale-[0.99] transition">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[16px] font-semibold text-slate-900 leading-tight">{p.titolo}</span>
                {i === 0 && gruppo.pratiche.length > 1 && <span className="text-[11px] font-semibold text-green-800 bg-green-100 rounded-full px-2 py-0.5">{tr('più recente')}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{p.persona}{p.data ? ` · ${p.data}` : ''}</div>
              <div className="mt-2"><StatoPratica p={p} /></div>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1 shrink-0"><FileText className="w-4 h-4" />{p.documenti.length}<ChevronRight className="w-5 h-5 text-slate-300" /></div>
          </button>
        ))}
      </div>
    );
  }

  // 1° livello: promemoria + un riquadro per servizio
  return (
    <div className="space-y-4">
      <Appuntamenti token={token} ospite={dati.ospite} />
      {dati.promemoria.map((m, i) => <Promemoria key={`p${i}`} m={m} onNascondi={nascondi} />)}
      {dati.promemoria_spenti && !dati.ospite && dati.pratiche.length > 0 && <ProponiConsenso tipo="promemoria" giaSpento token={token} onAttivato={carica} />}
      {dati.ospite && (
        <div className="bg-white rounded-3xl shadow-sm p-5">
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl bg-green-100 text-green-800 flex items-center justify-center shrink-0"><Info className="w-5 h-5" /></div>
            <div>
              <div className="text-[17px] font-semibold text-slate-900">{tr('Benvenuto!')}</div>
              <p className="text-[15px] text-slate-600 mt-1 leading-snug">{tr('Con questo cellulare o email non risultano ancora pratiche presso JV Service. Puoi leggere i nostri servizi e scriverci da Messaggi. Se sei già nostro cliente, chiedi allo studio di registrare questo contatto: vedrai qui le tue pratiche.')}</p>
            </div>
          </div>
        </div>
      )}
      {dati.ospite && <DiventaCliente token={token} />}
      {!dati.ospite && gruppi.length === 0 && (
        <div className="bg-white rounded-3xl p-6 text-center text-slate-500">
          <FolderOpen className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-2">{tr('Per ora non ci sono pratiche da mostrare.')}</p>
        </div>
      )}
      {gruppi.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm divide-y divide-slate-100 overflow-hidden">
          <div className="px-4 pt-4 pb-2 text-[13px] font-semibold tracking-wider text-slate-500 uppercase">{tr('Le tue pratiche')}</div>
          {gruppi.map((g) => (
            <button key={g.servizio} onClick={() => vai(() => setServizioSel(g.servizio))} className="w-full flex items-center gap-3 px-4 py-4 text-left active:bg-slate-50">
              <IconaServizio nome={g.servizio} grande />
              <div className="flex-1 min-w-0">
                <div className="text-[16px] font-semibold text-slate-900">{nomeServizio(g.servizio)}</div>
                <div className="text-sm text-slate-500 truncate">{g.pratiche.length} {g.pratiche.length === 1 ? tr('pratica') : tr('pratiche')} · {tr('ultima')}: {g.pratiche[0].titolo}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300" />
            </button>
          ))}
        </div>
      )}
      <Errore testo={errore} />
    </div>
  );
}

// ---------------- news ----------------
function TestoArticolo({ testo }) {
  // testo semplice: paragrafi separati da una riga vuota; righe "- " = elenco (mai HTML)
  return (testo || '').split(/\n\s*\n/).map((blocco, i) => {
    const righe = blocco.split('\n');
    if (righe.every((r) => r.trim().startsWith('- '))) {
      return <ul key={i} className="my-3 space-y-1.5">{righe.map((r, j) => (
        <li key={j} className="flex gap-2"><span className="text-green-700">•</span><span>{r.trim().slice(2)}</span></li>
      ))}</ul>;
    }
    return <p key={i} className="my-3 whitespace-pre-line">{blocco}</p>;
  });
}

// Galleria a schermo intero: si scorrono le immagini col dito (o con le frecce), immagine sempre intera.
function Galleria({ immagini, inizio = 0, onChiudi }) {
  const [i, setI] = useState(inizio);
  const fila = React.useRef(null);
  useEffect(() => { fila.current?.scrollTo({ left: inizio * fila.current.clientWidth }); }, [inizio]);
  const vai = (k) => fila.current?.scrollTo({ left: k * fila.current.clientWidth, behavior: 'smooth' });
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between px-3 py-3 text-white pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <button onClick={onChiudi} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        <span className="text-[15px] font-medium">{i + 1} / {immagini.length}</span>
        <span className="w-10" />
      </div>
      <div ref={fila} onScroll={(e) => setI(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
        className="flex-1 flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none]">
        {immagini.map((src, k) => (
          <div key={k} className="w-full h-full shrink-0 snap-center flex items-center justify-center p-2">
            <img src={`${BASE}${src}`} alt={`Immagine ${k + 1}`} className="max-w-full max-h-full object-contain" />
          </div>
        ))}
      </div>
      {immagini.length > 1 && (
        <div className="flex items-center justify-center gap-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button onClick={() => vai(Math.max(0, i - 1))} disabled={i === 0} className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center disabled:opacity-30"><ChevronLeft className="w-6 h-6" /></button>
          <div className="flex gap-1.5">{immagini.map((_, k) => <span key={k} className={`w-2 h-2 rounded-full ${k === i ? 'bg-white' : 'bg-white/30'}`} />)}</div>
          <button onClick={() => vai(Math.min(immagini.length - 1, i + 1))} disabled={i === immagini.length - 1} className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center disabled:opacity-30"><ChevronRight className="w-6 h-6" /></button>
        </div>
      )}
    </div>
  );
}

// miniature nell'elenco: immagine intera (non tagliata) su fondo chiaro
function Miniature({ immagini }) {
  if (!immagini?.length) return null;
  const viste = immagini.slice(0, 3);
  return (
    <div className="flex gap-2 mt-3">
      {viste.map((src, k) => (
        <div key={k} className="relative flex-1 h-20 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center">
          <img src={`${BASE}${src}`} alt="" className="max-w-full max-h-full object-contain" />
          {k === viste.length - 1 && immagini.length > 3 && (
            <span className="absolute inset-0 bg-black/45 text-white font-semibold flex items-center justify-center">+{immagini.length - 3}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// news già lette su questo telefono: il pallino rosso su "News" conta solo le nuove non ancora aperte
const CHIAVE_NEWS_LETTE = 'jv_area_clienti_news_lette';
const newsLette = () => { try { return JSON.parse(localStorage.getItem(CHIAVE_NEWS_LETTE) || '[]'); } catch (_) { return []; } };
function segnaNewsLetta(id) {
  if (!id) return;
  try { localStorage.setItem(CHIAVE_NEWS_LETTE, JSON.stringify([...newsLette().filter((x) => x !== id), id].slice(-300))); } catch (_) { /* privato */ }
  window.dispatchEvent(new Event('jv:news-lette'));
}
const newsRecente = (n) => {
  const [g, m, a] = (n.data || '').split('/').map(Number);
  return a && (Date.now() - new Date(a, m - 1, g).getTime()) < 7 * 24 * 3600 * 1000;
};

function News({ titolo, filtroIniziale = '' }) {
  const [dati, setDati] = useState(null);
  const [filtro, setFiltro] = useState(filtroIniziale);
  const [aperta, setAperta] = useState(null);
  const [galleria, setGalleria] = useState(null);   // indice dell'immagine aperta a schermo intero
  const tocco = React.useRef(null);
  const [archivio, setArchivio] = useState(null);   // news scadute (si caricano quando si apre l'archivio)
  const [vediArchivio, setVediArchivio] = useState(false);
  useEffect(() => {
    if (!vediArchivio) return;
    chiama(`/news/archivio${filtro ? `?servizio=${encodeURIComponent(filtro)}` : ''}`).then((d) => setArchivio(d.news)).catch(() => setArchivio([]));
  }, [vediArchivio, filtro]);
  useEffect(() => { setFiltro(filtroIniziale); }, [filtroIniziale]);
  useEffect(() => {
    chiama(`/news${filtro ? `?servizio=${encodeURIComponent(filtro)}` : ''}`).then(setDati).catch(() => setDati({ servizi: [], news: [] }));
  }, [filtro]);
  if (!dati) return null;

  if (aperta) {
    return (
      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        {galleria !== null && <Galleria immagini={aperta.immagini} inizio={galleria} onChiudi={() => setGalleria(null)} />}
        <div className="p-5">
          <button onClick={() => setAperta(null)} className="flex items-center text-slate-500 text-sm -ml-1 mb-3"><ChevronLeft className="w-4 h-4" /> {tr('News')}</button>
          <div className="flex items-center gap-2 text-xs text-slate-500"><IconaServizio nome={aperta.servizio} /> <span>{nomeServizio(aperta.servizio)} · {aperta.data}</span></div>
          {aperta.scaduta
            ? <div className="inline-flex items-center gap-1.5 mt-2 text-[13px] font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1"><Archive className="w-4 h-4" /> {aperta.scaduta_testo} {tr('· archivio')}</div>
            : aperta.validita && <div className="inline-flex items-center gap-1.5 mt-2 text-[13px] font-medium bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1"><CalendarDays className="w-4 h-4" /> {aperta.validita}</div>}
          <h2 className="text-[23px] font-bold text-slate-900 mt-3 leading-tight" style={TITOLO}>{tz(aperta, 'titolo')}</h2>
          {aperta.sommario && <p className="mt-2 text-[16px] text-slate-600 font-medium">{tz(aperta, 'sommario')}</p>}
          <div className="text-[16px] leading-relaxed text-slate-800"><TestoArticolo testo={tz(aperta, 'testo')} /></div>
          {aperta.immagini?.length > 0 && (
            <div className="mt-2 space-y-3">
              {aperta.immagini.map((src, k) => (
                <button key={k} onClick={() => setGalleria(k)} className="block w-full rounded-2xl overflow-hidden bg-slate-100 relative">
                  <img src={`${BASE}${src}`} alt={`Immagine ${k + 1}`} className="mx-auto w-auto max-w-full h-auto max-h-[65vh] object-contain" />
                  <span className="absolute bottom-2 right-2 bg-black/55 text-white text-[12px] rounded-full px-2.5 py-1 flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" /> {aperta.immagini.length > 1 ? `${k + 1}/${aperta.immagini.length}` : 'Ingrandisci'}</span>
                </button>
              ))}
            </div>
          )}
          <div className="mt-4 rounded-2xl bg-green-50 text-green-900 p-4 text-[15px]">{tr('Ti interessa questo servizio? Contatta JV Service o passa in ufficio: ti aiutiamo noi.')}</div>
        </div>
      </div>
    );
  }
  // news "nuova": pubblicata negli ultimi 7 giorni (data in formato gg/mm/aaaa)
  const nuova = (n) => {
    const [g, m, a] = (n.data || '').split('/').map(Number);
    return a ? (Date.now() - new Date(a, m - 1, g).getTime()) < 7 * 24 * 3600 * 1000 : false;
  };
  const nuove = dati.news.filter(nuova).length;
  const categorie = ['', ...dati.servizi];
  const cambiaCategoria = (verso) => {
    const k = categorie.indexOf(filtro) + verso;
    if (k >= 0 && k < categorie.length) {
      setFiltro(categorie[k]);
      document.getElementById(`cat-${k}`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };
  const inizioTocco = (e) => { tocco.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const fineTocco = (e) => {
    if (!tocco.current) return;
    const dx = e.changedTouches[0].clientX - tocco.current.x;
    const dy = e.changedTouches[0].clientY - tocco.current.y;
    tocco.current = null;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) cambiaCategoria(dx < 0 ? 1 : -1);   // solo gesti orizzontali netti
  };
  // le news cambiano di continuo: riquadro colorato, diverso dai servizi (fissi, bianchi)
  return (
    <div className="rounded-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-4 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <div className="relative w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow">
          <Megaphone className="w-5 h-5" />
          {nuove > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />}
        </div>
        <div className="flex-1">
          <div className="text-[17px] font-bold text-slate-900 leading-tight" style={TITOLO}>{tr('Novità dallo studio')}</div>
          <div className="text-[12px] text-amber-800">{nuove > 0 ? (nuove === 1 ? tr('1 nuova questa settimana') : tr('{n} nuove questa settimana', { n: nuove })) : tr('Bonus, scadenze e aggiornamenti')}</div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 [scrollbar-width:none]">
        {['', ...dati.servizi].map((s, k) => (
          <button key={s || 'tutti'} id={`cat-${k}`} onClick={() => setFiltro(s)}
            className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition ${filtro === s ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-amber-200'}`}>
            {s ? nomeServizio(s) : tr('Tutte')}
          </button>
        ))}
      </div>
      {dati.news.length === 0 && <div className="bg-white rounded-3xl p-6 text-center text-slate-500">{filtro ? tr('Nessuna news per questo servizio.') : tr('Nessuna news al momento.')}</div>}
      <div className="text-[11px] text-amber-800/70 px-1 mb-2">{tr('← scorri col dito per cambiare categoria →')}</div>
      {/* elenco su e giù; col dito a destra/sinistra si passa alla categoria successiva/precedente */}
      <div className="space-y-4" onTouchStart={inizioTocco} onTouchEnd={fineTocco}>
        {dati.news.map((n) => (
          <button key={n.id} onClick={() => { segnaNewsLetta(n.id); setAperta(n); }}
            className={`w-full text-left bg-white rounded-3xl shadow-sm overflow-hidden active:scale-[0.99] transition ${n.in_evidenza ? 'ring-2 ring-amber-400' : ''}`}>
            <div className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {nuova(n) && <span className="bg-red-500 text-white px-2 py-0.5 rounded-full font-bold text-[10px] tracking-wide">{tr('NUOVA')}</span>}
                {n.in_evidenza && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">{tr('In evidenza')}</span>}
                <span>{nomeServizio(n.servizio)} · {n.data}</span>
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 leading-snug">{tz(n, 'titolo')}</div>
              {n.sommario && <div className="text-[15px] text-slate-600 mt-1 line-clamp-2">{tz(n, 'sommario')}</div>}
              {n.validita && <div className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-medium text-amber-800 bg-amber-50 rounded-full px-2 py-0.5"><CalendarDays className="w-3.5 h-3.5" /> {n.validita}</div>}
              <Miniature immagini={n.immagini} />
              <div className="text-sm text-green-800 font-semibold mt-2 flex items-center">{tr('Leggi')} <ChevronRight className="w-4 h-4" /></div>
            </div>
          </button>
        ))}
      </div>
      {/* archivio: le news scadute restano leggibili */}
      <button onClick={() => setVediArchivio(!vediArchivio)} className="w-full mt-4 flex items-center justify-center gap-2 text-[13px] text-amber-900/70 py-2">
        <Archive className="w-4 h-4" /> {vediArchivio ? tr('Nascondi le news scadute') : tr('Archivio: news scadute')}
      </button>
      {vediArchivio && (archivio === null ? <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        : archivio.length === 0 ? <div className="text-center text-[13px] text-slate-500 pb-2">{tr('Nessuna news scaduta.')}</div> : (
          <div className="space-y-2">
            {archivio.map((n) => (
              <button key={n.id} onClick={() => { segnaNewsLetta(n.id); setAperta(n); }} className="w-full text-left bg-white/70 rounded-2xl p-3 border border-slate-200">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-semibold">{n.scaduta_testo}</span>
                  <span>{nomeServizio(n.servizio)}</span>
                </div>
                <div className="text-[15px] font-semibold text-slate-600 mt-1 leading-snug">{tz(n, 'titolo')}</div>
              </button>
            ))}
          </div>
        ))}
    </div>
  );
}

// ---------------- messaggi (richieste allo studio) ----------------
function Messaggi({ token, onEsci, onNonLetti, bozza, onBozzaUsata }) {
  const [dati, setDati] = useState(null);
  const [aperto, setAperto] = useState(null);
  const [nuova, setNuova] = useState(false);
  // argomento: si tocca un servizio dell'elenco; "Altro" apre il campo per scrivere l'oggetto
  const [form, setForm] = useState({ oggetto: '', servizio: '', testo: '', altro: false });
  const [testo, setTesto] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState('');

  const carica = useCallback(async () => {
    try {
      const d = await chiama('/ticket', { token });
      setDati(d); onNonLetti(d.ticket.reduce((t, x) => t + x.non_letti, 0));
    } catch (err) { if (err.status === 401) onEsci(); else setErrore(err.message); }
  }, [token, onEsci, onNonLetti]);
  useEffect(() => { carica(); }, [carica]);
  // arrivo da "Chiedi informazioni" su una pratica del catalogo: nuova richiesta già compilata
  useEffect(() => {
    if (!bozza) return;
    setNuova(true); setAperto(null);
    setForm((f) => ({ ...f, oggetto: bozza.oggetto, servizio: bozza.servizio, altro: false, testo: f.testo || '' }));
    onBozzaUsata();
  }, [bozza, onBozzaUsata]);
  const apri = async (id) => {
    try { setAperto(await chiama(`/ticket/${id}`, { token })); carica(); setTimeout(() => window.scrollTo({ top: document.body.scrollHeight }), 50); }
    catch (err) { setErrore(err.message); }
  };
  const invia = async (e) => {
    e.preventDefault(); setAttesa(true); setErrore('');
    try {
      if (nuova) {
        const oggetto = form.altro ? form.oggetto.trim() : (form.oggetto || form.servizio);
        const r = await chiama('/ticket', { metodo: 'POST', token, corpo: {
          oggetto, servizio: form.altro ? 'Pratiche e servizi' : form.servizio, testo: form.testo, nome: form.nome } });
        setNuova(false); setForm({ oggetto: '', servizio: '', testo: '', altro: false }); await apri(r.id);
      } else {
        await chiama(`/ticket/${aperto.id}/messaggi`, { metodo: 'POST', token, corpo: { testo } });
        setTesto(''); await apri(aperto.id);
      }
    } catch (err) { setErrore(err.message); } finally { setAttesa(false); }
  };
  const campo = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:bg-white';
  const pulsante = 'w-full py-3.5 rounded-2xl text-[16px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 disabled:opacity-40 flex items-center justify-center gap-2';

  if (!dati) return <div className="bg-white rounded-3xl flex justify-center py-16 text-slate-400"><Loader2 className="w-7 h-7 animate-spin" /></div>;

  if (nuova) {
    return (
      <form onSubmit={invia} className="bg-white rounded-3xl shadow-sm p-5 space-y-3">
        <button type="button" className="flex items-center text-slate-500 text-sm -ml-1" onClick={() => setNuova(false)}><ChevronLeft className="w-4 h-4" /> {tr('Messaggi')}</button>
        <h2 className="text-[21px] font-bold text-slate-900" style={TITOLO}>{tr('Nuova richiesta allo studio')}</h2>
        {dati.ospite && (
          <input className={campo} placeholder={tr('Nome e cognome')} maxLength={80} autoComplete="name"
            value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        )}
        <div className="text-[14px] font-medium text-slate-700">{tr('Di cosa ci vuoi scrivere?')}</div>
        <div className="flex flex-wrap gap-2">
          {dati.servizi.filter((s) => s !== 'Pratiche e servizi').map((s) => (
            <button type="button" key={s} onClick={() => setForm({ ...form, servizio: s, altro: false, oggetto: form.servizio === s ? form.oggetto : '' })}
              className={`px-3 py-1.5 rounded-full text-sm border ${!form.altro && form.servizio === s ? 'bg-green-800 text-white border-green-800' : 'bg-white text-slate-600 border-slate-200'}`}>{nomeServizio(s)}</button>
          ))}
          <button type="button" onClick={() => setForm({ ...form, altro: true, servizio: '', oggetto: '' })}
            className={`px-3 py-1.5 rounded-full text-sm border ${form.altro ? 'bg-green-800 text-white border-green-800' : 'bg-white text-slate-600 border-dashed border-slate-300'}`}>{tr('Altro…')}</button>
        </div>
        {form.altro && (
          <input className={campo} placeholder={tr("Scrivi l'argomento (es. Cambio residenza)")} maxLength={120} autoFocus
            value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} />
        )}
        {!form.altro && form.oggetto && form.oggetto !== form.servizio && (
          <div className="text-[13px] text-slate-500">Argomento: <b className="text-slate-700">{form.oggetto}</b></div>
        )}
        <textarea className={`${campo} min-h-[140px]`} placeholder={tr('Scrivi la tua domanda…')} maxLength={3000}
          value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} />
        <button className={pulsante} disabled={attesa || (form.altro ? form.oggetto.trim().length < 3 : !form.servizio) || !form.testo.trim() || (dati.ospite && (form.nome || '').trim().length < 3)}>
          {attesa ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} {tr('Invia allo studio')}
        </button>
        <Errore testo={errore} />
      </form>
    );
  }

  if (aperto) {
    return (
      <div className="bg-white rounded-3xl shadow-sm p-4">
        <button className="flex items-center text-slate-500 text-sm -ml-1" onClick={() => { setAperto(null); carica(); }}><ChevronLeft className="w-4 h-4" /> {tr('Messaggi')}</button>
        <div className="mt-2 flex items-start gap-3">
          <IconaServizio nome={aperto.servizio} />
          <div className="flex-1 min-w-0">
            <div className="text-[18px] font-semibold text-slate-900 leading-tight">{aperto.oggetto}</div>
            <div className="text-xs text-slate-500 mt-0.5">{nomeServizio(aperto.servizio)} · {tr(aperto.stato_testo)}</div>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {aperto.messaggi.map((m, i) => (
            <div key={i} className={`flex ${m.da === 'cliente' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${m.da === 'cliente' ? 'bg-green-700 text-white rounded-br-sm' : 'bg-slate-100 text-slate-900 rounded-bl-sm'}`}>
                <div className="whitespace-pre-wrap text-[15px]">{m.testo}</div>
                <div className={`text-[11px] mt-1 ${m.da === 'cliente' ? 'text-green-100' : 'text-slate-500'}`}>{m.autore} · {m.quando}</div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={invia} className="mt-4 flex gap-2 items-end">
          <textarea className={`${campo} min-h-[52px] flex-1`} rows={2} placeholder={tr('Rispondi…')} maxLength={3000} value={testo} onChange={(e) => setTesto(e.target.value)} />
          <button className="w-12 h-12 rounded-2xl bg-green-700 text-white flex items-center justify-center disabled:opacity-40 shrink-0" disabled={attesa || !testo.trim()}>
            {attesa ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </form>
        <Errore testo={errore} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button onClick={() => setNuova(true)} className="w-full bg-white rounded-3xl shadow-sm p-4 flex items-center gap-3 text-left active:scale-[0.99] transition">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-700 to-emerald-500 text-white flex items-center justify-center"><Plus className="w-6 h-6" /></div>
        <div className="flex-1">
          <div className="text-[17px] font-semibold text-slate-900">{tr('Scrivi allo studio')}</div>
          <div className="text-sm text-slate-500">{tr('Una domanda su una pratica o un servizio')}</div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300" />
      </button>
      {dati.ticket.length > 0 && <h3 className="text-[13px] font-semibold tracking-wider text-slate-500 uppercase px-1 pt-2">{tr('Le tue richieste')}</h3>}
      {dati.ticket.map((t) => (
        <button key={t.id} onClick={() => apri(t.id)} className="w-full bg-white rounded-3xl shadow-sm p-4 flex gap-3 text-left">
          <IconaServizio nome={t.servizio} grande />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[16px] font-semibold text-slate-900 truncate flex-1">{t.oggetto}</span>
              {t.non_letti > 0 && <span className="bg-red-600 text-white text-xs font-semibold rounded-full px-2 py-0.5">{t.non_letti}</span>}
            </div>
            <div className="text-sm text-slate-500 truncate">{t.ultimo}</div>
            <div className="text-xs text-slate-400 mt-1">{t.stato_testo} · {t.aggiornato}</div>
          </div>
        </button>
      ))}
      <Errore testo={errore} />
    </div>
  );
}

// ---------------- catalogo: servizio → categoria → voce (scheda con i documenti) ----------------
const voceCerca = (v) => [v.titolo, v.sommario, v.categoria, v.servizio, v.titolo_zh, v.sommario_zh, v.categoria_zh, ...(v.documenti || []).flatMap((d) => [d.nome, d.nome_zh])].filter(Boolean).join(' ').toLowerCase();

function RigaVoce({ v, onApri, conServizio }) {
  return (
    <button onClick={() => onApri(v)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-slate-50">
      {conServizio && <IconaServizio nome={v.servizio} />}
      <div className="flex-1 min-w-0">
        <div className="text-[16px] font-semibold text-slate-900 leading-snug">{tz(v, 'titolo')}</div>
        {v.sommario && <div className="text-sm text-slate-500 line-clamp-2">{tz(v, 'sommario')}</div>}
        {v.documenti?.length > 0 && <div className="text-xs text-green-800 mt-1 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {v.documenti.length} {tr('documenti da portare')}</div>}
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
    </button>
  );
}

function CatalogoServizio({ nome, categorie, onApri, onChiudi, onChiedi, entrato }) {
  const [q, setQ] = useState('');
  const filtro = q.trim().toLowerCase();
  const visibili = categorie.map((c) => ({ ...c, voci: c.voci.filter((v) => !filtro || voceCerca(v).includes(filtro)) })).filter((c) => c.voci.length);
  const totale = categorie.reduce((t, c) => t + c.voci.length, 0);
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-3xl shadow-sm p-4">
        <button onClick={onChiudi} className="flex items-center text-slate-500 text-sm -ml-1"><ChevronLeft className="w-4 h-4" /> {tr('Tutti i servizi')}</button>
        <div className="flex items-center gap-3 mt-3">
          <IconaServizio nome={nome} grande />
          <div>
            <h2 className="text-[22px] font-bold text-slate-900 leading-tight" style={TITOLO}>{nomeServizio(nome)}</h2>
            <div className="text-sm text-slate-500">{totale} {totale === 1 ? tr('pratica') : tr('pratiche')}{tr(': scegli la tua e guarda cosa portare')}</div>
          </div>
        </div>
        {totale > 4 && (
          <div className="mt-4 relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('Cerca (es. rinnovo, coesione…)')}
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-[16px] focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:bg-white" />
          </div>
        )}
      </div>
      {totale === 0 && (
        <div className="bg-white rounded-3xl shadow-sm p-6 text-center">
          <FolderOpen className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-2 text-[15px] text-slate-600">{tr('L\'elenco delle pratiche di questo servizio sarà disponibile a breve.')}</p>
          <p className="text-[14px] text-slate-500 mt-1">{tr('Intanto scrivici o chiamaci: ti diciamo noi cosa serve.')}</p>
          {onChiedi && (
            <button onClick={onChiedi} className="w-full mt-4 py-3 rounded-2xl text-[15px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 flex items-center justify-center gap-2">
              <MessageCircle className="w-5 h-5" /> {entrato ? tr('Scrivi allo studio') : tr('Entra e scrivi allo studio')}</button>
          )}
        </div>
      )}
      {totale > 0 && visibili.length === 0 && <div className="bg-white rounded-3xl p-6 text-center text-slate-500">{tr('Nessuna pratica trovata.')}</div>}
      {visibili.map((c) => (
        <div key={c.categoria || '-'}>
          {c.categoria && <h3 className="text-[13px] font-semibold tracking-wider text-slate-500 uppercase px-1 mb-2">{tz(c.voci[0] || {}, 'categoria') || c.categoria}</h3>}
          <div className="bg-white rounded-3xl shadow-sm divide-y divide-slate-100 overflow-hidden">
            {c.voci.map((v) => <RigaVoce key={v.id} v={v} onApri={onApri} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// spunte "ce l'ho" dei documenti: solo su questo telefono, per pratica del catalogo
const CHIAVE_SPUNTE = 'jv_area_clienti_documenti_pronti';
const leggiSpunte = (id) => { try { return JSON.parse(localStorage.getItem(CHIAVE_SPUNTE) || '{}')[id] || []; } catch (_) { return []; } };
const salvaSpunte = (id, elenco) => {
  try {
    const tutte = JSON.parse(localStorage.getItem(CHIAVE_SPUNTE) || '{}');
    if (elenco.length) tutte[id] = elenco; else delete tutte[id];
    localStorage.setItem(CHIAVE_SPUNTE, JSON.stringify(tutte));
  } catch (_) { /* privato */ }
};

function SchedaVoce({ v, onChiudi, onChiedi, entrato, token, onAccedi }) {
  const obbligatori = (v.documenti || []).filter((d) => d.obbligatorio);
  const seServe = (v.documenti || []).filter((d) => !d.obbligatorio);
  const [pronti, setPronti] = useState(() => leggiSpunte(v.id));   // nomi dei documenti che il cliente ha già
  const segna = (nome) => {
    const nuovi = pronti.includes(nome) ? pronti.filter((x) => x !== nome) : [...pronti, nome];
    setPronti(nuovi); salvaSpunte(v.id, nuovi);
  };
  const fatti = obbligatori.filter((d) => pronti.includes(d.nome)).length;
  const Doc = ({ d }) => {
    const ok = pronti.includes(d.nome);
    return (
      <li>
        <button type="button" onClick={() => segna(d.nome)} className="w-full flex gap-3 py-2.5 text-left">
          <span className={`mt-0.5 w-6 h-6 rounded-md border-2 shrink-0 flex items-center justify-center transition ${ok ? 'bg-green-600 border-green-600 text-white' : d.obbligatorio ? 'border-green-700 bg-white' : 'border-slate-300 bg-white'}`}>
            {ok && <Check className="w-4 h-4" strokeWidth={3} />}
          </span>
          <span>
            <span className={`text-[15px] ${ok ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{tz(d, 'nome')}</span>
            {d.nota && <span className="block text-[13px] text-slate-500 leading-snug">{tz(d, 'nota')}</span>}
          </span>
        </button>
      </li>
    );
  };
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      <button onClick={onChiudi} className="flex items-center text-slate-500 text-sm -ml-1"><ChevronLeft className="w-4 h-4" /> {nomeServizio(v.servizio)}</button>
      <div className="flex items-center gap-2 text-xs text-slate-500 mt-3"><IconaServizio nome={v.servizio} /> <span>{nomeServizio(v.servizio)}{v.categoria ? ` · ${tz(v, 'categoria')}` : ''}</span></div>
      <h2 className="text-[23px] font-bold text-slate-900 mt-2 leading-tight" style={TITOLO}>{tz(v, 'titolo')}</h2>
      {v.sommario && <p className="mt-2 text-[16px] text-slate-600">{tz(v, 'sommario')}</p>}
      {(v.tempi || v.costo) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {v.tempi && <span className="inline-flex items-center gap-1.5 text-[13px] bg-slate-100 text-slate-700 rounded-full px-3 py-1"><Clock className="w-4 h-4" /> {tz(v, 'tempi')}</span>}
          {v.costo && <span className="inline-flex items-center gap-1.5 text-[13px] bg-green-50 text-green-800 rounded-full px-3 py-1"><Euro className="w-4 h-4" /> {tz(v, 'costo')}</span>}
        </div>
      )}
      {v.descrizione && <div className="text-[16px] leading-relaxed text-slate-800"><TestoArticolo testo={tz(v, 'descrizione')} /></div>}
      {obbligatori.length > 0 && (
        <div className="mt-4 rounded-2xl border border-green-100 bg-green-50/40 p-4">
          <div className="font-semibold text-slate-900 flex items-center gap-2"><ListChecks className="w-5 h-5 text-green-700" /> {tr('Documenti da portare')}
            <span className={`ml-auto whitespace-nowrap shrink-0 text-[12px] font-semibold rounded-full px-2.5 py-0.5 ${fatti === obbligatori.length ? 'bg-green-600 text-white' : 'bg-white text-green-800 border border-green-200'}`}>
              {fatti === obbligatori.length ? tr('Tutto pronto ✓') : tr('{fatti} di {totale} pronti', { fatti, totale: obbligatori.length })}</span>
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">{tr('Tocca un documento quando ce l\'hai: resta segnato su questo telefono.')}</p>
          <ul className="mt-1 divide-y divide-green-100">{obbligatori.map((d, i) => <Doc key={i} d={d} />)}</ul>
        </div>
      )}
      {seServe.length > 0 && (
        <div className="mt-3 rounded-2xl border border-slate-100 p-4">
          <div className="font-semibold text-slate-900">{tr('Solo se ricorre il caso')}</div>
          <ul className="mt-1 divide-y divide-slate-100">{seServe.map((d, i) => <Doc key={i} d={d} />)}</ul>
        </div>
      )}
      <p className="text-[13px] text-slate-500 mt-3">{tr('Porta gli originali: le copie le facciamo noi. In caso di dubbio scrivici prima di venire.')}</p>
      {pronti.length > 0 && (
        <button type="button" onClick={() => { setPronti([]); salvaSpunte(v.id, []); }} className="mt-2 text-[13px] text-slate-500 underline">{tr('Togli tutte le spunte')}</button>
      )}
      {v.prenotazione && (
        <div className="mt-4">
          {token ? <Appuntamenti token={token} soloTipo={v.prenotazione} /> : (
            <button onClick={onAccedi} className="w-full py-3.5 rounded-2xl text-[16px] font-semibold text-green-800 border-2 border-green-700 flex items-center justify-center gap-2">
              <CalendarDays className="w-5 h-5" /> {tr('Entra e prenota l\'appuntamento')}</button>
          )}
        </div>
      )}
      <button onClick={() => onChiedi(v)}
        className="w-full mt-4 py-3.5 rounded-2xl text-[16px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 flex items-center justify-center gap-2">
        <MessageCircle className="w-5 h-5" /> {entrato ? tr('Chiedi informazioni allo studio') : tr('Entra e scrivi allo studio')}
      </button>
      <NonEntePubblico />
    </div>
  );
}

// Store (Google Play "informazioni governative", Apple 5.2/5.3): lo studio non è un ente pubblico; fonti ufficiali.
const FONTI_UFFICIALI = [['INPS', 'https://www.inps.it'], ['Agenzia delle Entrate', 'https://www.agenziaentrate.gov.it'],
  ['Portale Immigrazione', 'https://www.portaleimmigrazione.it'], ['Polizia di Stato', 'https://www.poliziadistato.it'],
  ['Ministero del Lavoro', 'https://www.lavoro.gov.it']];
function NonEntePubblico() {
  return (
    <p className="mt-4 text-[12px] text-slate-400 leading-snug">
      {tr("JV Service è uno studio privato di servizi: non è un ente pubblico e non rappresenta INPS, Agenzia delle Entrate, Questura o altre amministrazioni. Le informazioni sulle pratiche pubbliche vengono dai siti ufficiali:")}{' '}
      {FONTI_UFFICIALI.map(([n, u], i) => <React.Fragment key={u}>{i > 0 && ' · '}<a href={u} target="_blank" rel="noreferrer" className="underline">{n}</a></React.Fragment>)}.
    </p>
  );
}

// ---------------- servizi e contatti ----------------
function ServiziStudio({ onScegli, riquadro, catalogo = {}, onApriVoce }) {
  const elenco = ELENCO_SERVIZI.filter((x) => x.riquadro).map((x) => x.nome);
  const [q, setQ] = useState('');
  const filtro = q.trim().toLowerCase();
  const tutte = Object.values(catalogo).flat().flatMap((c) => c.voci);
  const trovate = filtro.length >= 2 ? tutte.filter((v) => voceCerca(v).includes(filtro)).slice(0, 20) : [];
  return (
    <div className={riquadro ? 'bg-white rounded-3xl shadow-sm p-4 pt-5' : ''}>
      <h3 className="text-[13px] font-semibold tracking-wider text-slate-500 uppercase px-1 mb-3">{tr('I nostri servizi')}</h3>
      {tutte.length > 0 && (
        <div className="mb-3">
          <div className="relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('Di cosa hai bisogno? (es. rinnovo permesso)')}
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-slate-200 bg-white text-[16px] shadow-sm focus:outline-none focus:ring-2 focus:ring-green-700/30" />
          </div>
          {filtro.length >= 2 && (
            <div className="mt-2 bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100 overflow-hidden">
              {trovate.length === 0 ? <div className="p-4 text-slate-500 text-[15px]">{tr('Nessuna pratica trovata: scrivici da Messaggi.')}</div>
                : trovate.map((v) => <RigaVoce key={v.id} v={v} onApri={onApriVoce} conServizio />)}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        {elenco.map((s) => {
          const { icona: Icona, colore } = servizio(s);
          return (
            <button key={s} onClick={() => onScegli(s)} className="bg-white rounded-2xl shadow-sm py-2.5 px-2 flex flex-col items-center gap-1.5 active:scale-95 transition">
              <div className={`${colore} w-9 h-9 rounded-xl flex items-center justify-center`}><Icona className="w-[18px] h-[18px]" /></div>
              <span className="text-[11.5px] text-slate-700 text-center leading-tight font-medium">{nomeServizio(s)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// QR WeChat dello studio a schermo intero, da salvare o condividere (nell'app: foglio di condivisione del telefono)
function QrWechat({ onChiudi }) {
  const [errore, setErrore] = useState('');
  const salva = async () => {
    setErrore('');
    try {
      const blob = await (await fetch('/logo-wechat-qr.png')).blob();
      const nome = 'WeChat-JV-Service.png';
      if (inApp()) {
        await chiediApp({ tipo: 'file', azione: 'scarica', nome, mime: 'image/png', base64: await aBase64(blob) });
      } else if (navigator.canShare && navigator.canShare({ files: [new File([blob], nome, { type: 'image/png' })] })) {
        await navigator.share({ files: [new File([blob], nome, { type: 'image/png' })], title: 'WeChat JV Service' });
      } else {
        const link = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = link; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(link), 5000);
      }
    } catch (err) { if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') setErrore(err.message); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/80 flex flex-col items-center justify-center p-5" onClick={onChiudi}>
      <div className="bg-white rounded-3xl p-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 font-bold text-[17px]"><MessageCircle className="w-5 h-5 text-green-700" /> WeChat JV Service</div>
          <button onClick={onChiudi} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <img src="/logo-wechat-qr.png" alt="WeChat JV Service" className="w-full rounded-xl" />
        <button onClick={salva} className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl bg-green-800 text-white font-semibold py-3">
          <Download className="w-5 h-5" /> {tr('Salva o condividi il codice')}
        </button>
        {errore && <p className="text-red-700 text-[13px] mt-2">{errore}</p>}
        <p className="text-[12px] text-slate-500 mt-2">{tr('Poi apri WeChat → Scansiona → Album e scegli l\'immagine salvata.')}</p>
      </div>
    </div>
  );
}

function Contatti() {
  const [studio, setStudio] = useState(null);
  const [informativa, setInformativa] = useState(false);
  const [qr, setQr] = useState(false);
  useEffect(() => { chiama('/studio').then(setStudio).catch(() => {}); }, []);
  if (!studio) return null;
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      <h3 className="text-[17px] font-bold text-slate-900" style={TITOLO}>{tr('Contatta lo studio')}</h3>
      <p className="text-sm text-slate-500 mt-0.5">{tr('Siamo a disposizione per ogni pratica.')}</p>
      <div className="mt-3 space-y-2">
        {studio.telefono_link && (
          <a href={studio.telefono_link} className="flex items-center gap-3 p-3 rounded-2xl bg-green-50 text-green-900">
            <Phone className="w-5 h-5" /> <span className="font-semibold">{studio.telefono}</span>
          </a>
        )}
        {studio.email && (
          <a href={`mailto:${studio.email}`} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 text-slate-800">
            <Mail className="w-5 h-5" /> <span>{studio.email}</span>
          </a>
        )}
        {studio.indirizzo && (
          <a href={`https://maps.google.com/?q=${encodeURIComponent(studio.indirizzo)}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 text-slate-800">
            <MapPin className="w-5 h-5 shrink-0" /> <span className="min-w-0 truncate whitespace-nowrap text-[13px] tracking-tight">{studio.indirizzo}</span>
          </a>
        )}
        <div className="flex items-center gap-4 p-3 rounded-2xl bg-slate-50 text-slate-800">
          <button type="button" onClick={() => setQr(true)} className="relative shrink-0" title={tr('Ingrandisci')}>
            <img src="/logo-wechat-qr.png" alt="WeChat JV Service" className="w-32 h-32 rounded-xl bg-white" />
            <span className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-white/90 shadow flex items-center justify-center"><Maximize2 className="w-4 h-4 text-slate-700" /></span>
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-semibold"><MessageCircle className="w-5 h-5 text-green-700" /> WeChat</div>
            <p className="text-[13px] text-slate-500 mt-1">{tr('Inquadra il codice con WeChat per aggiungerci. Dal telefono: tocca il codice per ingrandirlo e salvarlo.')}</p>
          </div>
        </div>
      </div>
      <button type="button" onClick={() => setInformativa(true)} className="mt-4 flex items-center gap-2 text-[14px] text-green-800 font-semibold">
        <ShieldCheck className="w-4 h-4" /> {tr('Informativa privacy')}
      </button>
      <NonEntePubblico />
      {informativa && <FinestraInformativa studio={studio} onChiudi={() => setInformativa(false)} />}
      {qr && <QrWechat onChiudi={() => setQr(false)} />}
    </div>
  );
}

// ---------------- privacy / GDPR ----------------
// Testo dell'informativa (art. 13 Reg. UE 2016/679). Se si cambia, aggiornare VERSIONE_PRIVACY nel backend:
// a tutti viene richiesto di nuovo il consenso.
function Informativa({ studio }) {
  if (lingua() === 'zh') return <InformativaZh studio={studio} />;
  const titolare = `${studio?.ragione_sociale || 'JV SERVICE SRLS'}${studio?.indirizzo ? `, ${studio.indirizzo}` : ''}${studio?.piva ? `, P.IVA ${studio.piva}` : ''}`;
  const contatti = [studio?.email, studio?.pec && `PEC ${studio.pec}`, studio?.telefono].filter(Boolean).join(' · ');
  const P = ({ t, children }) => (<div className="mt-3"><div className="font-semibold text-slate-900">{t}</div><div className="text-slate-600 mt-0.5 leading-snug">{children}</div></div>);
  return (
    <div className="text-[14px]">
      <div className="text-[18px] font-bold text-slate-900" style={TITOLO}>{tr('Informativa sul trattamento dei dati personali')}</div>
      <div className="text-xs text-slate-400">Art. 13 Regolamento UE 2016/679 (GDPR) · versione del {(studio?.versione_privacy || '').split('-').reverse().join('/')}</div>
      <P t="Titolare del trattamento">{titolare}. Contatti: {contatti}.</P>
      <P t="Quali dati trattiamo">{tr('Il numero di cellulare o l\'indirizzo email con cui entri, i messaggi che ci scrivi, le pratiche che hai affidato allo studio (per esempio Canone RAI, permessi di soggiorno, ospitalità, luce e gas, assicurazioni, telefonia) con i relativi documenti e ricevute, e i dati tecnici dell\'accesso (data, ora, indirizzo IP).')}</P>
      <P t="Perché e su quale base">
        a) farti entrare nell'area clienti, mostrarti le tue pratiche e i documenti, rispondere ai tuoi messaggi: esecuzione del servizio che hai richiesto (art. 6.1.b GDPR);<br />
        b) sicurezza degli accessi e adempimenti di legge (art. 6.1.c e 6.1.f GDPR);<br />
        c) solo se acconsenti: inviarti news e novità sui servizi dello studio (art. 6.1.a GDPR);<br />
        d) solo se acconsenti: ricordarti le scadenze delle tue pratiche, per esempio il rinnovo dell'esenzione del Canone RAI (art. 6.1.a GDPR);<br />
        e) se ci chiedi di diventare cliente («Diventa cliente»): registrarti in anagrafica con i dati che inserisci (art. 6.1.b GDPR);<br />
        f) se pubblichi annunci: pubblicarli, controllarli e gestire le segnalazioni (art. 6.1.b GDPR).</P>
      <P t="Annunci">Se pubblichi un annuncio trattiamo il testo, le foto (senza i dati di posizione GPS, che togliamo) e i contatti che scegli di indicare (telefono, WhatsApp, WeChat): l'annuncio e quei contatti sono <b>visibili a tutti</b>, anche a chi non è cliente. Prima della pubblicazione l'annuncio è controllato in automatico da un sistema di intelligenza artificiale che funziona sui server dello studio, senza inviare dati a terzi. Chi segnala un annuncio lascia il motivo e un indirizzo IP reso non riconoscibile, usato solo per evitare segnalazioni ripetute. Annunci e foto sono cancellati entro 6 mesi dall'eliminazione o dalla fine della pubblicazione.</P>
      <P t="Diventa cliente">Se scegli di inviarli, trattiamo nome, cognome, sesso, data e luogo di nascita, codice fiscale e indirizzo di residenza, per registrarti come cliente dello studio. L'invio è facoltativo: senza, puoi comunque usare news, servizi, messaggi e annunci.</P>
      <P t="Sono obbligatori?">I dati per le finalità a) e b) servono per usare l'area clienti: senza non è possibile entrare. I consensi c) e d) sono facoltativi: puoi negarli o ritirarli quando vuoi da <b>{tr('⚙ Impostazioni → Privacy e consensi')}</b>{tr(', senza conseguenze sulle tue pratiche.')}</P>
      <P t="Dove sono e chi li vede">{tr('I dati restano sui server dello studio JV Service, in Italia. Li vede solo il personale autorizzato dello studio. Il codice di accesso viene inviato per SMS dallo studio o per email tramite il fornitore di posta dello studio. Non vendiamo né cediamo i tuoi dati a terzi, non li trasferiamo fuori dall\'Unione Europea e non facciamo profilazione. I dati sono comunicati agli enti pubblici solo quando serve per la pratica che ci hai affidato.')}</P>
      <P t="Per quanto tempo">{tr('I dati delle pratiche per il tempo previsto dalla legge per la documentazione dello studio (di norma 10 anni); i registri degli accessi per 12 mesi; i consensi facoltativi fino a quando non li ritiri.')}</P>
      <P t="Cookie">{tr('L\'area clienti non usa cookie di profilazione né strumenti di statistica di terzi. Sul telefono viene salvata solo la sessione di accesso e la scelta su questi consensi, che sono necessarie al funzionamento.')}</P>
      <P t="I tuoi diritti">Puoi chiedere di accedere ai tuoi dati, correggerli, cancellarli, limitarne il trattamento, opporti, riceverli in un formato leggibile (artt. 15-22 GDPR) e ritirare i consensi, scrivendo a {studio?.email || 'lo studio'} {tr('o dalla sezione Messaggi. Puoi anche presentare reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).')}</P>
    </div>
  );
}

function Interruttore({ attivo, onCambia, titolo, testo, bloccato }) {
  return (
    <button type="button" disabled={bloccato} onClick={() => onCambia(!attivo)}
      className="w-full flex items-start gap-3 text-left py-3 disabled:cursor-default">
      <div className="flex-1">
        <div className="text-[15px] font-semibold text-slate-900">{titolo}{bloccato && <span className="ml-2 text-[11px] font-medium text-green-800 bg-green-100 rounded-full px-2 py-0.5 align-middle">{tr('obbligatorio')}</span>}</div>
        <div className="text-[13px] text-slate-500 leading-snug mt-0.5">{testo}</div>
      </div>
      <span className={`mt-1 w-11 h-6 rounded-full relative shrink-0 transition ${attivo ? 'bg-green-700' : 'bg-slate-300'} ${bloccato ? 'opacity-60' : ''}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${attivo ? 'left-[22px]' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

const TESTI_CONSENSI = {
  privacy: ['Informativa privacy e dati per il servizio', "Ho letto l'informativa. I dati servono per entrare, vedere le pratiche e scrivere allo studio."],
  news: ['News e novità sui servizi', "Ti avvisiamo di bonus, agevolazioni e nuove scadenze di legge (in app, email o SMS). Senza, non riceverai queste novità: potrai solo leggerle nell'app."],
  promemoria: ['Promemoria delle scadenze', "Ti ricordiamo in tempo le scadenze delle tue pratiche, per esempio il rinnovo del Canone RAI entro il 31 gennaio. Senza, nessun avviso: dovrai ricordartelo tu."],
};

// Schermata che compare al primo ingresso (e quando cambia l'informativa): obbligatorio + facoltativi.
function ConsensoPrivacy({ studio, iniziali, onConferma, inAttesa }) {
  const [scelte, setScelte] = useState({ news: !!iniziali?.news, promemoria: !!iniziali?.promemoria });
  const [letto, setLetto] = useState(false);
  const [leggi, setLeggi] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        {leggi ? (
          <>
            <button type="button" className="flex items-center text-slate-500 text-sm -ml-1 mb-2" onClick={() => setLeggi(false)}><ChevronLeft className="w-4 h-4" /> {tr('Torna ai consensi')}</button>
            <Informativa studio={studio} />
            <button type="button" onClick={() => { setLetto(true); setLeggi(false); }}
              className="w-full mt-5 py-3.5 rounded-2xl text-[16px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600">{tr('Ho letto')}</button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-green-100 text-green-800 flex items-center justify-center"><ShieldCheck className="w-6 h-6" /></div>
              <div>
                <div className="text-[19px] font-bold text-slate-900 leading-tight flex items-center gap-2" style={TITOLO}>{tr('La tua privacy')}<span className="ml-auto"><SceltaLingua piccola /></span></div>
                <div className="text-[13px] text-slate-500">{tr('Prima di continuare, scegli come trattiamo i tuoi dati')}</div>
              </div>
            </div>
            <p className="text-[14px] text-slate-600 mt-3 leading-snug">
              {tr('JV Service tratta i tuoi dati solo per i servizi che ci chiedi, sui propri server in Italia, senza cederli a nessuno.')}{' '}
              <button type="button" className="text-green-800 font-semibold underline" onClick={() => setLeggi(true)}>{tr('Leggi l\'informativa completa')}</button>
            </p>
            <div className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
              <label className="flex items-start gap-3 py-3">
                <input type="checkbox" className="mt-1 w-5 h-5 accent-green-700" checked={letto} onChange={(e) => setLetto(e.target.checked)} />
                <span>
                  <span className="text-[15px] font-semibold text-slate-900">{tr(TESTI_CONSENSI.privacy[0])}</span>
                  <span className="ml-2 text-[11px] font-medium text-green-800 bg-green-100 rounded-full px-2 py-0.5">{tr('obbligatorio')}</span>
                  <span className="block text-[13px] text-slate-500 leading-snug mt-0.5">{tr(TESTI_CONSENSI.privacy[1])}</span>
                </span>
              </label>
              {['news', 'promemoria'].map((k) => (
                <Interruttore key={k} attivo={scelte[k]} onCambia={(v) => setScelte({ ...scelte, [k]: v })}
                  titolo={<>{tr(TESTI_CONSENSI[k][0])} <span className="ml-1 text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 align-middle">{tr('facoltativo')}</span></>}
                  testo={tr(TESTI_CONSENSI[k][1])} />
              ))}
            </div>
            <p className="text-[12px] text-slate-400 mt-2">{tr('Puoi cambiare i consensi facoltativi quando vuoi da ⚙ Impostazioni → Privacy e consensi.')}</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button type="button" disabled={!letto || inAttesa} onClick={() => onConferma({ privacy: true, news: false, promemoria: false })}
                className="py-3.5 rounded-2xl text-[15px] font-semibold text-slate-700 bg-slate-100 disabled:opacity-40">{tr('Solo obbligatori')}</button>
              <button type="button" disabled={!letto || inAttesa} onClick={() => onConferma({ privacy: true, ...scelte })}
                className="py-3.5 rounded-2xl text-[15px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 disabled:opacity-40 flex items-center justify-center gap-2">
                {inAttesa && <Loader2 className="w-4 h-4 animate-spin" />} {tr('Conferma scelte')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Informativa in cinese semplificato (traduzione di cortesia: fa fede il testo italiano)
function InformativaZh({ studio }) {
  const titolare = `${studio?.ragione_sociale || 'JV SERVICE SRLS'}${studio?.indirizzo ? `，${studio.indirizzo}` : ''}${studio?.piva ? `，增值税号 ${studio.piva}` : ''}`;
  const contatti = [studio?.email, studio?.pec && `PEC ${studio.pec}`, studio?.telefono].filter(Boolean).join(' · ');
  const P = ({ titolo, children }) => (<div className="mt-3"><div className="font-semibold text-slate-900">{titolo}</div><div className="text-slate-600 mt-0.5 leading-relaxed">{children}</div></div>);
  return (
    <div className="text-[14px]">
      <div className="text-[18px] font-bold text-slate-900" style={TITOLO}>个人数据处理说明</div>
      <div className="text-xs text-slate-400">依据欧盟第 2016/679 号条例（GDPR）第 13 条 · 版本日期 {(studio?.versione_privacy || '').split('-').reverse().join('/')}</div>
      <div className="mt-2 text-xs text-amber-800 bg-amber-50 rounded-lg px-2 py-1.5">此为中文译本，仅供参考；如有不一致，以意大利语版本为准。</div>
      <P titolo="数据控制者">{titolare}。联系方式：{contatti}。</P>
      <P titolo="我们处理哪些数据">您登录时使用的手机号或电子邮箱、您发给我们的消息、您委托事务所办理的事项（例如 Canone RAI、居留许可、住宿登记、水电燃气、保险、电话网络）及相关文件和回执，以及登录的技术数据（日期、时间、IP 地址）。</P>
      <P titolo="处理目的和法律依据">
        a) 让您登录客户专区，查看您的事项和文件，回复您的消息：履行您所要求的服务（GDPR 第 6.1.b 条）；<br />
        b) 登录安全和法律义务（GDPR 第 6.1.c 条和第 6.1.f 条）；<br />
        c) 仅在您同意时：向您发送事务所的服务新闻和动态（GDPR 第 6.1.a 条）；<br />
        d) 仅在您同意时：提醒您事项的期限，例如 Canone RAI 免除的续办（GDPR 第 6.1.a 条）；<br />
        e) 如果您申请成为客户（“成为客户”）：用您填写的资料在客户档案中登记（GDPR 第 6.1.b 条）；<br />
        f) 如果您发布广告：发布、审核广告并处理举报（GDPR 第 6.1.b 条）。</P>
      <P titolo="广告">如果您发布广告，我们会处理广告文字、照片（我们会删除其中的 GPS 位置数据）以及您选择填写的联系方式（电话、WhatsApp、微信）：广告和这些联系方式<b>所有人都能看到</b>，包括非客户。广告上线前由在事务所服务器上运行的人工智能系统自动审核，不会向第三方发送数据。举报广告时会记录举报原因和经过处理、无法识别的 IP 地址，仅用于防止重复举报。广告和照片在删除或停止发布后 6 个月内清除。</P>
      <P titolo="成为客户">如果您选择提交，我们会处理您的姓名、性别、出生日期和地点、税号以及居住地址，用于将您登记为事务所客户。提交是可选的：不提交也可以使用新闻、服务、消息和广告。</P>
      <P titolo="是否必须提供">a) 和 b) 所需的数据是使用客户专区的前提，否则无法登录。c) 和 d) 的同意是可选的：您可以随时在 <b>⚙ 设置 → 隐私与同意</b> 中拒绝或撤回，不会影响您的事项。</P>
      <P titolo="数据存放在哪里，谁能看到">数据保存在 JV Service 事务所位于意大利的服务器上，只有事务所授权人员可以查看。登录验证码由事务所通过短信发送，或通过事务所的邮件服务商以电子邮件发送。我们不出售、不转让您的数据，不传输到欧盟以外，也不进行用户画像。仅在办理您委托的事项需要时，才会将数据提供给相关公共机构。</P>
      <P titolo="保存期限">事项数据按法律对事务所文件规定的期限保存（通常 10 年）；登录记录保存 12 个月；可选同意保存至您撤回为止。</P>
      <P titolo="Cookie">客户专区不使用画像类 Cookie，也不使用第三方统计工具。手机上只保存登录状态和您对这些同意的选择，这些是正常运行所必需的。</P>
      <P titolo="您的权利">您可以要求查阅、更正、删除您的数据，限制处理，提出反对，或以可读格式获取数据（GDPR 第 15–22 条），也可以撤回同意：请写信至 {studio?.email || '事务所'}，或通过“消息”联系我们。您也可以向意大利个人数据保护监管机构（Garante per la protezione dei dati personali，www.garanteprivacy.it）投诉。</P>
    </div>
  );
}

// Dentro l'app (Contatti): consensi salvati sul server, modificabili.
function PrivacyConsensi({ token, studio, onEsci }) {
  const [io, setIo] = useState(null);
  const [leggi, setLeggi] = useState(false);
  const [errore, setErrore] = useState('');
  useEffect(() => { chiama('/io', { token }).then(setIo).catch((err) => { if (err.status === 401) onEsci(); }); }, [token, onEsci]);
  const cambia = async (k, v) => {
    setErrore('');
    try {
      const c = await chiama('/consensi', { metodo: 'PUT', token, corpo: { privacy: true, news: io.consensi.news, promemoria: io.consensi.promemoria, [k]: v } });
      setIo({ ...io, consensi: c }); salvaPrivacy({ versione: c.versione, news: c.news, promemoria: c.promemoria });
    } catch (err) { setErrore(err.message); }
  };
  if (!io) return null;
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      <h3 className="text-[17px] font-bold text-slate-900 flex items-center gap-2" style={TITOLO}><ShieldCheck className="w-5 h-5 text-green-700" /> {tr('Privacy e consensi')}</h3>
      <p className="text-sm text-slate-500 mt-0.5">{tr('Sei entrato con')} {io.contatto}{io.ospite ? tr(' (non ancora cliente)') : io.persone.length ? ` · ${io.persone.map((p) => p.nome).join(', ')}` : ''}.</p>
      <div className="mt-2 divide-y divide-slate-100">
        <Interruttore attivo bloccato titolo={tr(TESTI_CONSENSI.privacy[0])} testo={tr('Accettata il {data}', { data: io.consensi.quando })} onCambia={() => {}} />
        {['news', 'promemoria'].map((k) => (
          <Interruttore key={k} attivo={io.consensi[k]} onCambia={(v) => cambia(k, v)} titolo={tr(TESTI_CONSENSI[k][0])} testo={tr(TESTI_CONSENSI[k][1])} />
        ))}
      </div>
      <button type="button" className="mt-2 text-[15px] text-green-800 font-semibold" onClick={() => setLeggi(!leggi)}>{leggi ? tr('Chiudi informativa') : tr("Leggi l'informativa privacy")}</button>
      {leggi && <div className="mt-3 border-t border-slate-100 pt-3"><Informativa studio={studio} /></div>}
      <p className="text-[12px] text-slate-400 mt-3">{tr('Per cancellare i tuoi dati o esercitare gli altri diritti scrivi allo studio da Messaggi.')}</p>
      <Errore testo={errore} />
    </div>
  );
}

// Dopo l'accesso col codice: proposta di attivare l'impronta su questo telefono.
function PropostaImpronta({ token, onFatto }) {
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState('');
  const attiva = async () => {
    setAttesa(true); setErrore('');
    try {
      const io = await chiama('/io', { token });
      await attivaImpronta(token, io.contatto);
      onFatto(true);
    } catch (err) {
      setErrore(err.name === 'NotAllowedError' ? tr('Annullato. Puoi riprovare quando vuoi da ⚙ Impostazioni.') : (err.message || tr('Attivazione non riuscita')));
    } finally { setAttesa(false); }
  };
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      <div className="flex gap-3 items-start">
        <div className="w-12 h-12 rounded-2xl bg-green-100 text-green-800 flex items-center justify-center shrink-0"><Fingerprint className="w-7 h-7" /></div>
        <div className="flex-1">
          <div className="text-[17px] font-semibold text-slate-900">{tr('Entra con impronta o Face ID')}</div>
          <p className="text-[14px] text-slate-500 mt-0.5 leading-snug">{tr('La prossima volta su questo telefono entri con il dito o il volto, senza aspettare il codice.')}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <button onClick={() => { try { localStorage.setItem(CHIAVE_BIO_NO, '1'); } catch (_) { /* privato */ } onFatto(false); }}
          className="py-3 rounded-2xl text-[15px] font-semibold text-slate-600 bg-slate-100">{tr('Non ora')}</button>
        <button onClick={attiva} disabled={attesa}
          className="py-3 rounded-2xl text-[15px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 disabled:opacity-50 flex items-center justify-center gap-2">
          {attesa && <Loader2 className="w-4 h-4 animate-spin" />} {tr('Attiva')}</button>
      </div>
      <Errore testo={errore} />
    </div>
  );
}

function ImprontaImpostazioni({ token, onEsci }) {
  const [dati, setDati] = useState(null);
  const [puo, setPuo] = useState(false);
  const [bio, setBio] = useState(leggiBio());
  const [errore, setErrore] = useState('');
  const [attesa, setAttesa] = useState(false);
  const carica = useCallback(() => chiama('/biometria', { token }).then(setDati).catch((err) => { if (err.status === 401) onEsci(); }), [token, onEsci]);
  useEffect(() => { carica(); bioDisponibile().then(setPuo); }, [carica]);
  const attiva = async () => {
    setAttesa(true); setErrore('');
    try { const io = await chiama('/io', { token }); await attivaImpronta(token, io.contatto); setBio(leggiBio()); carica(); }
    catch (err) { setErrore(err.name === 'NotAllowedError' ? tr('Annullato.') : (err.message || tr('Attivazione non riuscita'))); }
    finally { setAttesa(false); }
  };
  const togli = async (d) => {
    setErrore('');
    try {
      await chiama(`/biometria/${d.id}`, { metodo: 'DELETE', token });
      if (bio?.id === d.id) {
        if (bio.app) { await chiediApp({ tipo: 'togli_impronta' }).catch(() => {}); window.JV_APP = { ...(window.JV_APP || {}), dispositivo: null }; }
        salvaBio(null); setBio(null); salvaToken(token);
      }
      carica();
    } catch (err) { setErrore(err.message); }
  };
  if (!dati) return null;
  const questo = bio && dati.dispositivi.some((d) => d.id === bio.id);
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      <h3 className="text-[17px] font-bold text-slate-900 flex items-center gap-2" style={TITOLO}><Fingerprint className="w-5 h-5 text-green-700" /> {tr('Impronta o Face ID')}</h3>
      <p className="text-sm text-slate-500 mt-0.5">{questo ? tr('Attiva su questo telefono: entri senza codice.') : tr('Entra con il dito o il volto invece del codice.')}</p>
      {!questo && puo && (
        <button onClick={attiva} disabled={attesa}
          className="w-full mt-3 py-3 rounded-2xl text-[15px] font-semibold text-white bg-gradient-to-r from-green-800 to-green-600 disabled:opacity-50 flex items-center justify-center gap-2">
          {attesa ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-5 h-5" />} {tr('Attiva su questo telefono')}</button>
      )}
      {!questo && !puo && <p className="text-[13px] text-slate-400 mt-2">{tr('Su questo dispositivo non è disponibile (serve un telefono con impronta o Face ID, da clienti.crm-jv.com).')}</p>}
      {dati.dispositivi.length > 0 && (
        <div className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
          {dati.dispositivi.map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-2.5">
              <Smartphone className="w-5 h-5 text-slate-400" />
              <div className="flex-1 text-[14px]">
                <div className="text-slate-900">{d.dispositivo}{bio?.id === d.id ? ' (questo)' : ''}</div>
                <div className="text-xs text-slate-400">{tr('Attivata il {data}', { data: d.creato })}{d.ultimo_uso ? ` · ${tr('usata il {data}', { data: d.ultimo_uso })}` : ''}</div>
              </div>
              <button onClick={() => togli(d)} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center" title="Togli"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[12px] text-slate-400 mt-2">{tr('Telefono perso o cambiato? Togli qui il vecchio, oppure chiedi allo studio.')}</p>
      <Errore testo={errore} />
    </div>
  );
}

// ---------------- impostazioni: dimensione della visualizzazione ----------------
const CHIAVE_SCALA = 'jv_area_clienti_scala';
const SCALE = [['Piccolo', 0.9], ['Normale', 1], ['Grande', 1.12], ['Molto grande', 1.25]];
const leggiScala = () => { try { return Number(localStorage.getItem(CHIAVE_SCALA)) || 1; } catch (_) { return 1; } };
function applicaScala(v) { document.body.style.zoom = v === 1 ? '' : String(v); }   // ingrandisce tutta la pagina in proporzione

// "Elimina account" (Apple 5.1.1 v): cancella i dati dell'account app; pratiche e documenti restano allo studio
function EliminaAccount({ token, onEliminato }) {
  const [conferma, setConferma] = useState(false);
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState('');
  const elimina = async () => {
    setAttesa(true); setErrore('');
    try {
      await chiama('/account', { metodo: 'DELETE', token });
      salvaBio(null); salvaPrivacy(null);
      if (window.JV_APP) { await chiediApp({ tipo: 'togli_impronta' }).catch(() => {}); window.JV_APP = { ...window.JV_APP, dispositivo: null }; }
      onEliminato();
    } catch (err) { setErrore(err.message); setAttesa(false); }
  };
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      <h3 className="text-[17px] font-bold text-slate-900 flex items-center gap-2" style={TITOLO}><Trash2 className="w-5 h-5 text-red-600" /> {tr('Elimina account')}</h3>
      {!conferma ? (
        <>
          <p className="text-[13px] text-slate-500 mt-1 leading-snug">{tr("Cancella il tuo account dell'app: accesso, impronta / Face ID, consensi e notifiche. Le pratiche e i documenti restano allo studio, come previsto dalla legge.")}</p>
          <button type="button" onClick={() => setConferma(true)} className="mt-3 w-full py-3 rounded-2xl text-[15px] font-semibold text-red-700 bg-red-50 border border-red-100">{tr('Elimina il mio account')}</button>
        </>
      ) : (
        <>
          <p className="text-[14px] text-slate-700 mt-1 leading-snug">{tr("Sei sicuro? Uscirai dall'app e il tuo account verrà eliminato. Lo studio riceverà un avviso. Potrai entrare di nuovo in futuro con un nuovo codice.")}</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button type="button" onClick={() => setConferma(false)} disabled={attesa} className="py-3 rounded-2xl text-[15px] font-semibold text-slate-700 bg-slate-100">{tr('Annulla')}</button>
            <button type="button" onClick={elimina} disabled={attesa} className="py-3 rounded-2xl text-[15px] font-semibold text-white bg-red-600 flex items-center justify-center gap-2 disabled:opacity-50">
              {attesa && <Loader2 className="w-4 h-4 animate-spin" />} {tr('Elimina')}</button>
          </div>
        </>
      )}
      <Errore testo={errore} />
    </div>
  );
}

function Impostazioni({ onChiudi, token, studio, onEsci }) {
  const [informativa, setInformativa] = useState(false);
  const [scala, setScala] = useState(leggiScala());
  const scegli = (v) => { setScala(v); applicaScala(v); try { localStorage.setItem(CHIAVE_SCALA, String(v)); } catch (_) { /* privato */ } };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center" onClick={onChiudi}>
      <div className="bg-[#f3f6f4] w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white rounded-3xl p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center"><Settings className="w-5 h-5" /></div>
          <div className="flex-1 text-[18px] font-bold text-slate-900" style={TITOLO}>{tr('Impostazioni')}</div>
          <button onClick={onChiudi} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-4 text-[13px] font-semibold tracking-wider text-slate-500 uppercase">Lingua · 语言</div>
        <div className="mt-2"><SceltaLingua /></div>
        <div className="mt-4 text-[13px] font-semibold tracking-wider text-slate-500 uppercase">{tr('Dimensione del testo')}</div>
        <div className="grid grid-cols-4 gap-2 mt-2">
          {SCALE.map(([nome, v], k) => (
            <button key={v} onClick={() => scegli(v)}
              className={`rounded-2xl border-2 py-3 flex flex-col items-center gap-1 transition ${scala === v ? 'border-green-700 bg-green-50 text-green-900' : 'border-slate-200 text-slate-600'}`}>
              <span className="font-bold leading-none" style={{ fontSize: 14 + k * 4 }}>{tr('Aa')}</span>
              <span className="text-[11px] font-medium text-center leading-tight">{tr(nome)}</span>
            </button>
          ))}
        </div>
        <p className="text-[12px] text-slate-400 mt-3">{tr('Vale per tutta l\'app e resta salvata su questo telefono.')}</p>
        </div>
        {token && <ImprontaImpostazioni token={token} onEsci={onEsci} />}
        {token && <PrivacyConsensi token={token} studio={studio} onEsci={onEsci} />}
        {token && <EliminaAccount token={token} onEliminato={onEsci} />}
        {!token && <p className="text-[13px] text-slate-500 text-center px-4">{tr('Entra nell\'area clienti per gestire impronta / Face ID e i consensi privacy.')}</p>}
        {!token && <button type="button" onClick={() => setInformativa(true)} className="w-full flex items-center justify-center gap-2 text-[15px] text-green-800 font-semibold">
          <ShieldCheck className="w-4 h-4" /> {tr('Informativa privacy')}</button>}
        {informativa && <FinestraInformativa studio={studio} onChiudi={() => setInformativa(false)} />}
      </div>
    </div>
  );
}

// barra in basso, come un'app. Nell'app lo spazio dei tasti di Android/iPhone lo lascia già l'app.
function BarraBasso({ voci, attiva, onScegli }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200"
      style={{ paddingBottom: inApp() ? 0 : 'env(safe-area-inset-bottom)' }}>
      <div className="max-w-xl mx-auto grid" style={{ gridTemplateColumns: `repeat(${voci.length}, minmax(0, 1fr))` }}>
        {voci.map(([k, t, Icona, conta]) => (
          <button key={k} onClick={() => onScegli(k)}
            className={`relative flex flex-col items-center gap-0.5 pt-1.5 pb-1 text-[11px] font-medium ${attiva === k ? 'text-green-800' : 'text-slate-400'}`}>
            <Icona className="w-5 h-5" /> {t}
            {conta > 0 && (
              <span className="absolute top-0.5 left-1/2 ml-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{conta}</span>
            )}
          </button>
        ))}
      </div>
    </nav>
  );
}

// ---------------- pagina ----------------
function Testata({ sottotitolo, onEsci, onAccedi, onImpostazioni, onNotifiche, onContatti, nonLette = 0 }) {
  return (
    <div className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-600 text-white pt-4 pb-12 px-4 relative overflow-hidden">
      <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-white/5" />
      <div className="absolute right-16 top-20 w-24 h-24 rounded-full bg-white/5" />
      <div className="max-w-xl mx-auto flex items-center gap-3 relative">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg p-1">
          <img src="/logo-jv-marchio.png" alt="JV Service" className="w-full h-full object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[18px] font-bold leading-tight whitespace-nowrap truncate" style={TITOLO}>JV Service <span className="font-normal opacity-80">益民</span></div>
          <div className="text-[12px] opacity-80 truncate">{sottotitolo}</div>
        </div>
        {onNotifiche && (
          <button onClick={onNotifiche} className="relative w-10 h-10 shrink-0 rounded-full bg-white/15 flex items-center justify-center" title="Notifiche">
            <BellRing className="w-5 h-5" />
            {nonLette > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{nonLette}</span>}
          </button>
        )}
        {onContatti && (
          <button onClick={onContatti} className="w-10 h-10 shrink-0 rounded-full bg-white/15 flex items-center justify-center" title={tr('Contatti')}>
            <Phone className="w-5 h-5" />
          </button>
        )}
        {onImpostazioni && (
          <button onClick={onImpostazioni} className="w-10 h-10 shrink-0 rounded-full bg-white/15 flex items-center justify-center" title={tr('Impostazioni')}>
            <Settings className="w-5 h-5" />
          </button>
        )}
        {onAccedi && (
          <button onClick={onAccedi} className="shrink-0 whitespace-nowrap flex items-center gap-1.5 rounded-full bg-white text-green-900 font-semibold text-[14px] pl-3 pr-3.5 py-2 shadow">
            <UserRound className="w-4 h-4" /> {tr('Area clienti')}
          </button>
        )}
        {onEsci && (
          <button onClick={onEsci} className="w-10 h-10 shrink-0 rounded-full bg-white/15 flex items-center justify-center" title="Esci">
            <LogOut className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function AreaClienti() {
  const q = new URLSearchParams(window.location.search);
  if (q.has('privacy')) return <PaginaPrivacy />;
  if (q.has('elimina-account')) return <PaginaEliminaAccount />;
  return <AppAreaClienti />;
}

function AppAreaClienti() {
  const [linguaApp, setLinguaApp] = useState(() => { const l = linguaIniziale(); impostaLingua(l, false); return l; });
  useEffect(() => {
    const cambia = (e) => { impostaLingua(e.detail); setLinguaApp(e.detail); };
    window.addEventListener('jv:lingua', cambia);
    return () => window.removeEventListener('jv:lingua', cambia);
  }, []);
  const [token, setToken] = useState(leggiToken());
  const [scheda, setScheda] = useState('news');     // dopo l'accesso: news, servizi, messaggi, pratiche, contatti
  const [sezione, setSezione] = useState('news');   // prima dell'accesso: news, servizi, contatti
  const [newsNuove, setNewsNuove] = useState(0);
  const [filtroNews, setFiltroNews] = useState('');
  const [catalogo, setCatalogo] = useState({});
  const [bozza, setBozza] = useState(null);
  const [paginaAperta, setPaginaAperta] = useState(null);
  const [nonLetti, setNonLetti] = useState(0);
  const [studio, setStudio] = useState(null);
  const [privacy, setPrivacy] = useState(leggiPrivacy());
  const [rinnovo, setRinnovo] = useState(false);   // dopo l'accesso: consenso da dare per la versione nuova
  const [salvoConsensi, setSalvoConsensi] = useState(false);
  const [proponiImpronta, setProponiImpronta] = useState(false);
  // senza accesso: la home è la vetrina (servizi, news, contatti); il login si apre da "Area clienti" in alto.
  // Chi ha già l'impronta attiva su questo telefono parte direttamente dal "Bentornato".
  const [vediAccesso, setVediAccesso] = useState(!!leggiBio());
  const [impostazioni, setImpostazioni] = useState(false);
  const [notifiche, setNotifiche] = useState(false);
  const [notificheNuove, setNotificheNuove] = useState(0);
  useEffect(() => {
    if (!token) return undefined;
    const leggi = () => chiama('/notifiche', { token }).then((d) => setNotificheNuove(d.non_lette || 0)).catch(() => {});
    leggi();
    const t = setInterval(leggi, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [token]);
  const azzeraNotifiche = useCallback(() => setNotificheNuove(0), []);
  useEffect(() => { applicaScala(leggiScala()); }, []);
  useEffect(() => {
    chiama('/news').then((d) => {
      // nessuna news pubblicata: si parte da Servizi (mai una prima schermata vuota)
      if (!(d.news || []).length) { setSezione((x) => (x === 'news' ? 'servizi' : x)); setScheda((x) => (x === 'news' ? 'servizi' : x)); }
      return d;
    }).then((d) => {
      const conta = () => { const lette = newsLette(); setNewsNuove((d.news || []).filter((n) => newsRecente(n) && !lette.includes(n.id)).length); };
      conta();
      window.addEventListener('jv:news-lette', conta);   // la pagina resta aperta per tutta la sessione
    }).catch(() => {});
  }, []);
  const pannelloImpostazioni = impostazioni && <Impostazioni onChiudi={() => setImpostazioni(false)} token={token} studio={studio}
    onEsci={() => { setImpostazioni(false); esci(); }} />;
  const esci = useCallback(() => { salvaToken(''); setToken(''); }, []);
  const [, setServiziPronti] = useState(0);
  useEffect(() => {
    chiama('/elenco-servizi').then((d) => { ELENCO_SERVIZI = d.servizi || []; setServiziPronti((n) => n + 1); }).catch(() => {});
    chiama('/catalogo').then((d) => setCatalogo(d.catalogo || {})).catch(() => {});
  }, []);
  useEffect(() => { chiama('/studio').then(setStudio).catch(() => {}); }, []);
  useEffect(() => {
    if (!token || leggiBio()) return;
    let no = false;
    try { no = localStorage.getItem(CHIAVE_BIO_NO) === '1'; } catch (_) { /* privato */ }
    if (!no) bioDisponibile().then(setProponiImpronta);
  }, [token]);
  useEffect(() => {
    if (!token) return;
    chiama('/io', { token }).then((d) => setRinnovo(!!d.consensi?.da_rinnovare)).catch((err) => { if (err.status === 401) esci(); });
  }, [token, esci]);
  const versione = studio?.versione_privacy;
  // il consenso si chiede solo a chi entra (nell'accesso) o, se l'informativa cambia, a chi è già entrato
  const serveConsenso = !!versione && !!token && rinnovo;
  const confermaConsensi = async (c) => {
    if (token) {
      setSalvoConsensi(true);
      try { await chiama('/consensi', { metodo: 'PUT', token, corpo: c }); setRinnovo(false); } catch (err) { if (err.status === 401) esci(); }
      finally { setSalvoConsensi(false); }
    }
    const scelta = { versione, news: c.news, promemoria: c.promemoria };
    salvaPrivacy(scelta); setPrivacy(scelta);
  };
  const finestraConsenso = serveConsenso && <ConsensoPrivacy studio={studio} iniziali={privacy} onConferma={confermaConsensi} inAttesa={salvoConsensi} />;
  useEffect(() => {
    if (!token) return;
    chiama('/ticket', { token }).then((d) => setNonLetti(d.ticket.reduce((t, x) => t + x.non_letti, 0))).catch(() => {});
  }, [token]);
  // riquadro di un servizio: il suo catalogo (categorie → pratiche)
  const scegliServizio = (s, nonEntrato) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // sempre la pagina fissa del servizio (mai le news): se il catalogo è vuoto mostra come contattarci
    setPaginaAperta({ servizio: s }); if (!nonEntrato) setScheda('servizi');
  };
  const apriVoce = (v) => { setPaginaAperta((p) => ({ voce: v, da: p?.servizio ? p : null })); window.scrollTo({ top: 0 }); };
  const chiedi = (v) => {
    setPaginaAperta(null);
    if (token) { setBozza({ oggetto: v.titolo, servizio: v.servizio }); setScheda('messaggi'); } else setVediAccesso(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const usaBozza = useCallback(() => setBozza(null), []);
  const vistaCatalogo = paginaAperta && (paginaAperta.voce
    ? <SchedaVoce v={paginaAperta.voce} entrato={!!token} onChiedi={chiedi} token={token}
        onAccedi={() => { setPaginaAperta(null); setVediAccesso(true); window.scrollTo({ top: 0 }); }}
        onChiudi={() => setPaginaAperta(paginaAperta.da || { servizio: paginaAperta.voce.servizio })} />
    : <CatalogoServizio nome={paginaAperta.servizio} categorie={catalogo[paginaAperta.servizio] || []} onApri={apriVoce} onChiudi={() => setPaginaAperta(null)}
        onChiedi={() => chiedi({ titolo: tr('Informazioni su {servizio}', { servizio: nomeServizio(paginaAperta.servizio) }), servizio: paginaAperta.servizio })} entrato={!!token} />);
  const vaiANews = (s) => scegliServizio(s, false);

  if (!token) {
    return (
      <div key={linguaApp} className="min-h-screen bg-[#f3f6f4] pb-20">
        {finestraConsenso}
        {pannelloImpostazioni}
        <Testata sottotitolo={tr(vediAccesso ? 'Area clienti' : { servizi: 'I nostri servizi', news: 'Novità dallo studio', contatti: 'Contatti', annunci: 'Annunci' }[sezione])} onImpostazioni={() => setImpostazioni(true)}
 />
        <div className="max-w-xl mx-auto px-4 -mt-8 relative space-y-5">
          {vediAccesso ? (
            <>
              <Accesso onEntrato={setToken} consensi={privacy} studio={studio} />
              <button onClick={() => setVediAccesso(false)} className="w-full flex items-center justify-center gap-1 text-[15px] text-slate-500 py-2">
                <ChevronLeft className="w-4 h-4" /> {tr('Torna ai servizi')}
              </button>
            </>
          ) : paginaAperta ? vistaCatalogo : (
            <>
              {sezione === 'servizi' && <ServiziStudio onScegli={(s) => scegliServizio(s, true)} catalogo={catalogo} onApriVoce={apriVoce} riquadro />}
              {sezione === 'news' && <News titolo="" filtroIniziale={filtroNews} />}
              {sezione === 'contatti' && <Contatti />}
              {sezione === 'annunci' && <Annunci token="" onAccedi={() => { setVediAccesso(true); window.scrollTo({ top: 0 }); }} />}
            </>
          )}
        </div>
        <BarraBasso attiva={vediAccesso ? 'accedi' : sezione}
          voci={[['news', tr('News'), Megaphone, newsNuove], ['servizi', tr('Servizi'), LayoutGrid], ['annunci', tr('Annunci'), Store], ['contatti', tr('Contatti'), Phone], ['accedi', tr('Area clienti'), UserRound]]}
          onScegli={(k) => {
            setPaginaAperta(null); window.scrollTo({ top: 0 });
            if (k === 'accedi') setVediAccesso(true); else { setVediAccesso(false); setSezione(k); }
          }} />
      </div>
    );
  }

  return (
    <div key={linguaApp} className="min-h-screen bg-[#f3f6f4] pb-20">
      {finestraConsenso}
      <Testata sottotitolo={tr({ servizi: 'I nostri servizi', news: 'Novità dallo studio', pratiche: 'Le tue pratiche', messaggi: 'Messaggi con lo studio', contatti: 'Contatti', annunci: 'Annunci' }[scheda])} onEsci={esci} onImpostazioni={() => setImpostazioni(true)}
        onContatti={() => { setScheda('contatti'); setPaginaAperta(null); window.scrollTo({ top: 0 }); }}
        onNotifiche={() => setNotifiche(true)} nonLette={notificheNuove} />
      {pannelloImpostazioni}
      {notifiche && <PannelloNotifiche token={token} onChiudi={() => setNotifiche(false)} onLette={azzeraNotifiche} />}
      <div className="max-w-xl mx-auto px-4 -mt-10 relative space-y-6">
        {proponiImpronta && <PropostaImpronta token={token} onFatto={() => setProponiImpronta(false)} />}
        <DiventaCliente token={token} automatico />
        {scheda === 'pratiche' && <Pratiche token={token} onEsci={esci} />}
        {scheda === 'messaggi' && <Messaggi token={token} onEsci={esci} onNonLetti={setNonLetti} bozza={bozza} onBozzaUsata={usaBozza} />}
        {scheda === 'servizi' && (paginaAperta ? vistaCatalogo : (
          <>
            <ProponiConsenso tipo="news" token={token} />
            <ServiziStudio onScegli={vaiANews} riquadro catalogo={catalogo} onApriVoce={apriVoce} />
          </>
        ))}
        {scheda === 'news' && <News titolo="" filtroIniziale={filtroNews} />}
        {scheda === 'contatti' && <Contatti />}
        {scheda === 'annunci' && <Annunci token={token} onAccedi={() => {}} />}
      </div>
      <BarraBasso attiva={scheda} onScegli={(k) => { setScheda(k); setPaginaAperta(null); window.scrollTo({ top: 0 }); }}
        voci={[['news', tr('News'), Megaphone, newsNuove], ['servizi', tr('Servizi'), LayoutGrid], ['annunci', tr('Annunci'), Store],
          ['messaggi', tr('Messaggi'), MessageCircle, nonLetti], ['pratiche', tr('Pratiche'), FolderOpen]]} />
    </div>
  );
}
